import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const EX_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { 
  ChevronLeft, ChevronRight, Volume2, VolumeX, Check, X, 
  Shuffle, RotateCcw, Lightbulb, Target, BookOpen, 
  GripVertical, Play, Pause, Award, Zap, Brain,
  ArrowRight, RefreshCw, CheckCircle, AlertCircle
} from 'lucide-react';

// ==================== FLASHCARD COMPONENT ====================
const FlashCard = ({ front, back, onNext, onPrev, current, total, onSpeak }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  const handleSwipe = (direction) => {
    if (direction === 'left' && onNext) onNext();
    if (direction === 'right' && onPrev) onPrev();
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      handleSwipe(diff > 0 ? 'left' : 'right');
    }
    setTouchStart(null);
  };

  useEffect(() => {
    setIsFlipped(false);
  }, [current]);

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Progress */}
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10.5px] uppercase font-semibold" style={{ letterSpacing: '0.2em', color: '#C5B9AE' }}>Karte {current + 1} von {total}</span>
        <div className="flex gap-1">
          {Array.from({ length: total }).map((_, i) => (
            <div 
              key={i} 
              className={`w-2 h-2 rounded-full transition-colors ${i === current ? 'bg-terra' : i < current ? 'bg-[#D89232]' : 'bg-cream-300'}`}
            />
          ))}
        </div>
      </div>

      {/* Card */}
      <div 
        className="relative h-[300px] perspective-1000 cursor-pointer"
        onClick={() => setIsFlipped(!isFlipped)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className={`absolute inset-0 transition-transform duration-[600ms] transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
          {/* Front — Cream mit Doppel-Gold-Rahmen */}
          <div className="absolute inset-0 backface-hidden p-2" style={{ backgroundColor: '#FBF6F0', borderRadius: 4, border: '1px solid rgba(216,146,50,0.35)' }}>
            <div className="w-full h-full flex flex-col items-center justify-center px-6 relative" style={{ border: '1px solid rgba(216,146,50,0.35)', borderRadius: 2 }}>
              <p className="font-display text-[24px] md:text-[28px] text-center text-espresso leading-snug">{front}</p>
              <p className="absolute bottom-5 font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.24em', color: '#8F650F' }}>
                Tippen zum Umdrehen
              </p>
            </div>
          </div>
          
          {/* Back — Espresso-Karte */}
          <div className="absolute inset-0 backface-hidden rotate-y-180 rounded-[4px] flex flex-col items-center justify-center px-8" style={{ backgroundColor: '#2E2B27' }}>
            <div className="absolute top-0 left-8 right-8" style={{ height: 1, backgroundColor: '#D89232' }} />
            <p className="text-lg text-center leading-relaxed" style={{ color: '#F5F0EA' }}>{back}</p>
            {onSpeak && (
              <button 
                onClick={(e) => { e.stopPropagation(); onSpeak(back); }}
                className="absolute bottom-4 right-4 p-2 rounded-full transition-colors"
                style={{ border: '1px solid rgba(216,146,50,0.5)' }}
              >
                <Volume2 className="w-4 h-4" style={{ color: '#D89232' }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <button 
          onClick={onPrev}
          disabled={current === 0}
          className="group flex items-center gap-1.5 text-[13px] font-semibold text-sand-500 hover:text-terra disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
          Zurück
        </button>
        
        <button 
          onClick={() => setIsFlipped(!isFlipped)}
          className="p-2.5 rounded-full bg-transparent transition-colors"
          style={{ border: '1px solid rgba(216,146,50,0.6)' }}
          title="Karte umdrehen"
        >
          <RotateCcw className="w-4 h-4" style={{ color: '#8F650F' }} />
        </button>

        <button 
          onClick={onNext}
          className="h-11 px-7 rounded-full bg-transparent text-[12px] font-bold uppercase inline-flex items-center gap-2 transition-colors"
          style={{ border: '1.5px solid #E07A5F', color: '#E07A5F', letterSpacing: '0.18em' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E07A5F'; e.currentTarget.style.color = '#F5F0EA'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#E07A5F'; }}
        >
          {current === total - 1 ? 'Abschließen' : 'Weiter'} →
        </button>
      </div>
    </div>
  );
};

// ==================== MINI CHECKPOINT COMPONENT ====================
const MiniCheckpoint = ({ question, options, correctIndex, explanation, onComplete, onSpeak }) => {
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const handleSelect = (index) => {
    if (showResult) return;
    setShowWarning(false);
    setSelected(index);
    setShowResult(true);
    
    // Auto-continue after 3 seconds if correct
    if (index === correctIndex) {
      setTimeout(() => onComplete(true), 2500);
    }
  };

  const handleContinue = () => {
    // User wants to continue without answering - show warning first
    if (selected === null && !showResult) {
      setShowWarning(true);
      return;
    }
    onComplete(false);
  };

  const isCorrect = selected === correctIndex;

  return (
    <div className="w-full max-w-2xl mx-auto bg-cream-100 rounded-2xl border border-cream-300 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-sand-800">Schnell-Check!</h3>
          <p className="text-xs text-sand-500">Teste dein Verständnis</p>
        </div>
      </div>

      <p className="text-lg text-sand-800 mb-6 leading-relaxed">{question}</p>

      <div className="space-y-3">
        {options.map((option, idx) => {
          let bgColor = 'bg-cream-200 hover:bg-cream-300';
          let borderColor = 'border-transparent';
          let icon = null;

          if (showResult) {
            if (idx === correctIndex) {
              bgColor = 'bg-[#D89232]/10';
              borderColor = 'border-[#D89232]';
              icon = <Check className="w-5 h-5 text-[#D89232]" />;
            } else if (idx === selected && idx !== correctIndex) {
              bgColor = 'bg-terra/10';
              borderColor = 'border-terra';
              icon = <X className="w-5 h-5 text-terra" />;
            }
          }

          return (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              disabled={showResult}
              className={`w-full flex items-center justify-between p-4 rounded-xl border-2 ${bgColor} ${borderColor} transition-all text-left`}
            >
              <span className="text-sand-500">{option}</span>
              {icon}
            </button>
          );
        })}
      </div>

      {showResult && (
        <div className={`mt-6 p-4 rounded-xl ${isCorrect ? 'bg-[#D89232]/10 border border-[#D89232]/40' : 'bg-peach-50 border border-peach-200'}`}>
          <div className="flex items-start gap-3">
            {isCorrect ? (
              <CheckCircle className="w-6 h-6 text-[#D89232] flex-shrink-0" />
            ) : (
              <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
            )}
            <div>
              <p className={`font-semibold mb-1 ${isCorrect ? 'text-[#D89232]' : 'text-peach-dark'}`}>
                {isCorrect ? 'Richtig!' : 'Nicht ganz...'}
              </p>
              <p className={`text-sm 'text-espresso/80'`}>{explanation}</p>
            </div>
          </div>
          
          {!isCorrect && (
            <button
              onClick={handleContinue}
              className="mt-4 w-full px-4 py-2 bg-cream-300 text-sand-500 rounded-lg hover:text-terra transition-colors"
            >
              Weiter lernen →
            </button>
          )}
        </div>
      )}

      {/* Warning when trying to skip without answering */}
      {showWarning && !showResult && (
        <div className="mt-6 p-4 rounded-xl bg-amber-900/20 border border-amber-500/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
            <div>
              <p className="font-semibold mb-1 text-amber-400">Bitte wähle eine Antwort!</p>
              <p className="text-sm text-amber-200">Klicke auf eine der Optionen oben, um fortzufahren.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DRAG & DROP COMPONENT ====================
const DragDropExercise = ({ title, items, onComplete }) => {
  const [leftItems, setLeftItems] = useState(items.map(i => ({ ...i, matched: false })));
  const [rightItems, setRightItems] = useState(
    [...items].sort(() => Math.random() - 0.5).map(i => ({ ...i, matched: false }))
  );
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [matches, setMatches] = useState([]);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    if (selectedLeft !== null && selectedRight !== null) {
      const leftItem = leftItems[selectedLeft];
      const rightItem = rightItems[selectedRight];
      
      const isCorrect = leftItem.id === rightItem.id;
      
      if (isCorrect) {
        setLeftItems(prev => prev.map((item, i) => i === selectedLeft ? { ...item, matched: true } : item));
        setRightItems(prev => prev.map((item, i) => i === selectedRight ? { ...item, matched: true } : item));
        setMatches(prev => [...prev, { left: selectedLeft, right: selectedRight, correct: true }]);
      } else {
        setMatches(prev => [...prev, { left: selectedLeft, right: selectedRight, correct: false }]);
        // Flash wrong answer
        setTimeout(() => {
          setMatches(prev => prev.filter(m => m.correct));
        }, 1000);
      }
      
      setSelectedLeft(null);
      setSelectedRight(null);
      
      // Check if all matched
      const allMatched = leftItems.filter(i => !i.matched).length <= 1;
      if (allMatched && isCorrect) {
        setTimeout(() => {
          setShowResult(true);
          setTimeout(() => onComplete(true), 2000);
        }, 500);
      }
    }
  }, [selectedLeft, selectedRight]);

  const reset = () => {
    setLeftItems(items.map(i => ({ ...i, matched: false })));
    setRightItems([...items].sort(() => Math.random() - 0.5).map(i => ({ ...i, matched: false })));
    setSelectedLeft(null);
    setSelectedRight(null);
    setMatches([]);
    setShowResult(false);
  };

  return (
    <div className="w-full max-w-3xl mx-auto bg-cream-100 rounded-2xl border border-cream-300 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <Shuffle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-sand-800">{title}</h3>
            <p className="text-xs text-sand-500">Verbinde die passenden Paare</p>
          </div>
        </div>
        <button onClick={reset} className="p-2 rounded-lg bg-cream-300 text-sand-500 hover:text-terra">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {showResult ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-[#D89232]/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-[#D89232]" />
          </div>
          <h4 className="text-2xl font-bold text-terra mb-2">Perfekt!</h4>
          <p className="text-sand-500">Alle Paare richtig zugeordnet!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          {/* Left column */}
          <div className="space-y-3">
            {leftItems.map((item, idx) => (
              <button
                key={`left-${idx}`}
                onClick={() => !item.matched && setSelectedLeft(idx)}
                disabled={item.matched}
                className={`w-full p-4 rounded-xl text-left transition-all ${
                  item.matched 
                    ? 'bg-[#D89232]/10 border-2 border-[#D89232] text-espresso' 
                    : selectedLeft === idx 
                      ? 'bg-terra/20 border-2 border-terra text-sand-800' 
                      : 'bg-cream-200 border-2 border-transparent hover:border-cream-300 text-sand-500'
                }`}
              >
                {item.term}
              </button>
            ))}
          </div>

          {/* Right column */}
          <div className="space-y-3">
            {rightItems.map((item, idx) => (
              <button
                key={`right-${idx}`}
                onClick={() => !item.matched && setSelectedRight(idx)}
                disabled={item.matched}
                className={`w-full p-4 rounded-xl text-left transition-all ${
                  item.matched 
                    ? 'bg-[#D89232]/10 border-2 border-[#D89232] text-espresso' 
                    : selectedRight === idx 
                      ? 'bg-terra/20 border-2 border-terra text-sand-800' 
                      : 'bg-cream-200 border-2 border-transparent hover:border-cream-300 text-sand-500'
                }`}
              >
                {item.definition}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== FILL IN THE BLANK COMPONENT ====================
const FillInTheBlank = ({ sentence, blank, options, onComplete, explanation }) => {
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const handleSelect = (option) => {
    if (showResult) return;
    setSelected(option);
    setShowResult(true);
    
    if (option === blank) {
      setTimeout(() => onComplete(true), 2000);
    }
  };

  const parts = sentence.split('___');
  const isCorrect = selected === blank;

  return (
    <div className="w-full max-w-2xl mx-auto bg-cream-100 rounded-2xl border border-cream-300 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <Target className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-sand-800">Lückentext</h3>
          <p className="text-xs text-sand-500">Ergänze das fehlende Wort</p>
        </div>
      </div>

      <p className="text-lg text-sand-800 mb-6 leading-relaxed">
        {parts[0]}
        <span className={`inline-block min-w-[100px] px-3 py-1 mx-1 rounded-lg border-2 border-dashed ${
          showResult 
            ? isCorrect 
              ? 'border-[#D89232] bg-[#D89232]/10 text-espresso' 
              : 'border-terra bg-terra/10 text-espresso'
            : 'border-terra bg-terra/10'
        }`}>
          {selected || '?'}
        </span>
        {parts[1]}
      </p>

      <div className="flex flex-wrap gap-3 mb-6">
        {options.map((option, idx) => (
          <button
            key={idx}
            onClick={() => handleSelect(option)}
            disabled={showResult}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${
              showResult && option === blank
                ? 'bg-[#D89232] text-white'
                : showResult && option === selected && option !== blank
                  ? 'bg-terra text-white'
                  : selected === option
                    ? 'bg-terra text-white'
                    : 'bg-cream-300 text-sand-500 hover:bg-cream-300 hover:text-sand-800'
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      {showResult && (
        <div className={`p-4 rounded-xl ${isCorrect ? 'bg-[#D89232]/10 border border-[#D89232]/40' : 'bg-peach-50 border border-peach-200'}`}>
          <p className={`font-semibold mb-1 ${isCorrect ? 'text-[#D89232]' : 'text-peach-dark'}`}>
            {isCorrect ? 'Richtig!' : `Die richtige Antwort ist: ${blank}`}
          </p>
          <p className={`text-sm 'text-espresso/80'`}>{explanation}</p>
          
          {!isCorrect && (
            <button
              onClick={() => onComplete(false)}
              className="mt-4 px-4 py-2 bg-cream-300 text-sand-500 rounded-lg hover:text-terra transition-colors"
            >
              Verstanden, weiter →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== SCENARIO COMPONENT ====================
const ScenarioLearning = ({ scenario, question, choices, onComplete }) => {
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);

  const handleChoice = (choice) => {
    setSelectedChoice(choice);
    setShowOutcome(true);
  };

  const handleContinue = () => {
    onComplete(selectedChoice?.isCorrect || false);
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-cream-100 rounded-2xl border border-cream-300 overflow-hidden">
      {/* Scenario Header */}
      <div className="bg-gradient-to-r from-terra/20 to-transparent p-6 border-b border-cream-300">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-sand-800">Praxis-Szenario</h3>
            <p className="text-xs text-sand-500">Was würdest du tun?</p>
          </div>
        </div>
        <p className="text-sand-800 leading-relaxed">{scenario}</p>
      </div>

      <div className="p-6">
        {!showOutcome ? (
          <>
            <p className="text-lg font-semibold text-terra mb-4">{question}</p>
            <div className="space-y-3">
              {choices.map((choice, idx) => (
                <button
                  key={idx}
                  onClick={() => handleChoice(choice)}
                  className="w-full p-4 rounded-xl bg-cream-200 border-2 border-transparent hover:border-terra/50 text-left transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-cream-300 group-hover:bg-terra/20 flex items-center justify-center text-sand-500 group-hover:text-terra font-semibold transition-colors">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="text-sand-500 group-hover:text-sand-800 transition-colors">{choice.text}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className={`p-4 rounded-xl ${selectedChoice?.isCorrect ? 'bg-[#D89232]/10 border border-[#D89232]/40' : 'bg-peach-50 border border-peach-200'}`}>
              <div className="flex items-start gap-3">
                {selectedChoice?.isCorrect ? (
                  <Award className="w-6 h-6 text-[#D89232] flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-semibold mb-2 ${selectedChoice?.isCorrect ? 'text-[#D89232]' : 'text-peach-dark'}`}>
                    {selectedChoice?.isCorrect ? 'Gute Entscheidung!' : 'Nicht optimal...'}
                  </p>
                  <p className={`text-sm 'text-espresso/80'`}>
                    {selectedChoice?.outcome}
                  </p>
                </div>
              </div>
            </div>

            {selectedChoice?.learningPoint && (
              <div className="p-4 rounded-xl bg-terra/10 border border-terra/30">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-terra flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-terra mb-1">Lernpunkt:</p>
                    <p className="text-sm text-sand-500">{selectedChoice?.learningPoint}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleContinue}
              className="w-full py-3 rounded-xl bg-terra text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Weiter lernen →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== AUDIO SECTION COMPONENT ====================
const AudioSection = ({ title, content, onSpeak, isPlaying, onComplete }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="w-full max-w-2xl mx-auto bg-cream-100 rounded-2xl border border-cream-300 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-terra to-terra-dark flex items-center justify-center">
              <Volume2 className="w-5 h-5 text-sand-800" />
            </div>
            <div>
              <h3 className="font-bold text-sand-800">{title}</h3>
              <p className="text-xs text-sand-500">Die Dozentin erklärt</p>
            </div>
          </div>
          
          <button
            onClick={() => onSpeak(content)}
            data-testid="audio-section-play-btn"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              isPlaying 
                ? 'bg-terra/10 text-terra border border-terra/50' 
                : 'bg-terra text-white hover:opacity-90'
            }`}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            {isPlaying ? 'Stoppen' : 'Anhören'}
          </button>
        </div>

        <div className={`transition-all duration-300 ${expanded ? 'max-h-[500px]' : 'max-h-24'} overflow-hidden relative`}>
          <p className="text-sand-500 leading-relaxed">{content}</p>
          {!expanded && (
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-sm text-terra hover:underline"
        >
          {expanded ? 'Weniger anzeigen' : 'Text anzeigen...'}
        </button>
      </div>

      <div className="px-6 pb-6">
        <button
          onClick={onComplete}
          className="w-full py-3 rounded-xl bg-cream-300 text-sand-500 hover:bg-cream-300 hover:text-sand-800 transition-colors flex items-center justify-center gap-2"
        >
          Verstanden, weiter
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ==================== MAIN INTERACTIVE LEARNING COMPONENT ====================
// ==================== ZUORDNUNGS-ÜBUNG (KI-generiert) ====================
const MatchingStep = ({ title, instruction, pairs = [], onComplete }) => {
  const [shuffledRight, setShuffledRight] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [matched, setMatched] = useState({});
  const [wrongFlash, setWrongFlash] = useState(null);
  const [mistakes, setMistakes] = useState(0);

  useEffect(() => {
    setShuffledRight([...pairs].map(p => p.right).sort(() => Math.random() - 0.5));
    setMatched({});
    setSelectedLeft(null);
    setMistakes(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  const allMatched = pairs.length > 0 && Object.keys(matched).length === pairs.length;

  const handleRightClick = (right) => {
    if (selectedLeft === null || Object.values(matched).includes(right)) return;
    const pair = pairs.find(p => p.left === selectedLeft);
    if (pair && pair.right === right) {
      const next = { ...matched, [selectedLeft]: right };
      setMatched(next);
      setSelectedLeft(null);
      if (Object.keys(next).length === pairs.length) {
        setTimeout(() => onComplete(mistakes === 0), 900);
      }
    } else {
      setWrongFlash(right);
      setMistakes(m => m + 1);
      setTimeout(() => setWrongFlash(null), 600);
    }
  };

  return (
    <div className="w-full max-w-3xl bg-cream-100 rounded-2xl border border-cream-300 p-6 sm:p-8 shadow-soft">
      <p className="font-mono text-[10px] uppercase font-semibold mb-1" style={{ letterSpacing: '0.24em', color: '#8F650F' }}>
        Zuordnung
      </p>
      <h3 className="font-display text-[22px] text-espresso">{title}</h3>
      <p className="text-sm text-sand-600 mt-1 mb-6">{instruction || 'Ordne die Begriffe der passenden Beschreibung zu.'}</p>

      <div className="grid grid-cols-2 gap-4 sm:gap-6">
        {/* Begriffe */}
        <div className="space-y-2.5">
          {pairs.map((p) => {
            const isMatched = matched[p.left] !== undefined;
            const isSelected = selectedLeft === p.left;
            return (
              <button
                key={p.left}
                onClick={() => !isMatched && setSelectedLeft(isSelected ? null : p.left)}
                disabled={isMatched}
                className="w-full text-left px-4 py-3 rounded-xl text-[13.5px] font-medium transition-all duration-200"
                style={{
                  border: isMatched ? '1.5px solid #D89232' : isSelected ? '1.5px solid #E07A5F' : '1px solid #E0D6CA',
                  backgroundColor: isMatched ? 'rgba(216,146,50,0.12)' : isSelected ? 'rgba(169,84,47,0.08)' : '#FAF7F1',
                  color: isMatched ? '#8A8175' : '#2B2118',
                }}
                data-testid={`match-left-${p.left}`}
              >
                {isMatched && <Check className="w-3.5 h-3.5 inline mr-1.5" style={{ color: '#8F650F' }} />}
                {p.left}
              </button>
            );
          })}
        </div>

        {/* Beschreibungen (gemischt) */}
        <div className="space-y-2.5">
          {shuffledRight.map((right) => {
            const isUsed = Object.values(matched).includes(right);
            const isWrong = wrongFlash === right;
            return (
              <button
                key={right}
                onClick={() => handleRightClick(right)}
                disabled={isUsed}
                className="w-full text-left px-4 py-3 rounded-xl text-[13px] transition-all duration-200"
                style={{
                  border: isUsed ? '1.5px solid #D89232' : isWrong ? '1.5px solid #E07A5F' : '1px solid #E0D6CA',
                  backgroundColor: isUsed ? 'rgba(216,146,50,0.12)' : isWrong ? 'rgba(169,84,47,0.15)' : '#FAF7F1',
                  color: isUsed ? '#8A8175' : '#4A443E',
                  cursor: selectedLeft === null && !isUsed ? 'default' : undefined,
                  opacity: selectedLeft === null && !isUsed ? 0.75 : 1,
                }}
                data-testid={`match-right`}
              >
                {right}
              </button>
            );
          })}
        </div>
      </div>

      {allMatched ? (
        <p className="font-display italic text-[15px] mt-6 text-center" style={{ color: '#E07A5F' }} data-testid="matching-done">
          ✳ Alles richtig zugeordnet{mistakes > 0 ? ` — nach ${mistakes} ${mistakes === 1 ? 'Versuch' : 'Versuchen'} korrigiert.` : ' — im ersten Anlauf!'}
        </p>
      ) : (
        <p className="text-[12px] text-sand-500 mt-6 text-center">
          {selectedLeft ? `„${selectedLeft}" gewählt — jetzt die passende Beschreibung antippen.` : 'Erst links einen Begriff wählen, dann rechts die Beschreibung.'}
        </p>
      )}
    </div>
  );
};

const InteractiveLearning = ({ 
  moduleData, 
  courseId,
  onComplete, 
  authHeader = {},
  onSpeak 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [dbExercises, setDbExercises] = useState([]);
  const [exLoaded, setExLoaded] = useState(false);
  const audioRef = useRef(null);

  // KI-generierte Übungen (Fallszenarien + Zuordnungen) aus der DB laden
  useEffect(() => {
    if (!moduleData?.id) { setExLoaded(true); return; }
    axios.get(`${EX_API}/user/interactive-exercises/${moduleData.id}`, authHeader)
      .then(r => setDbExercises(r.data.exercises || []))
      .catch(() => setDbExercises([]))
      .finally(() => setExLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleData?.id]);

  // Generate interactive content from module data
  const generateLearningSteps = () => {
    const steps = [];
    const moduleOrder = moduleData?.order || 1;

    // Flashcards from key points - only include cards with proper question:answer format
    if (moduleData?.key_points?.length > 0) {
      // Filter out generic placeholders and keep only valid question:answer pairs
      const genericPlaceholders = [
        "Wichtige Konzepte", "Praktische Anwendung", "Prüfungsrelevant",
        "Pflichtfortbildung", "Zertifikat bei 70%", "Jährliche Pflichtfortbildung",
        "Wundmanagement Rezertifizierung"
      ];
      
      const flashcards = moduleData.key_points
        .filter(point => {
          // Skip generic placeholder key_points
          if (genericPlaceholders.includes(point)) return false;
          // Keep only points with a colon (question:answer format)
          return point.includes(':');
        })
        .map((point) => {
          const colonIndex = point.indexOf(':');
          return {
            front: point.substring(0, colonIndex).trim(),
            back: point.substring(colonIndex + 1).trim()
          };
        });
      
      // Only add flashcards step if we have valid cards
      if (flashcards.length > 0) {
        steps.push({ type: 'flashcards', data: { cards: flashcards } });
      }
    }

    // Modulspezifische KI-Übungen haben Vorrang — generische Kurs-Übungen
    // nur als Fallback, wenn für dieses Modul keine KI-Übungen existieren.
    const hasAiExercises = dbExercises.length > 0;

    if (!hasAiExercises) {
      // Mini checkpoints based on module AND course
      const checkpoints = getModuleCheckpoints(moduleOrder, courseId);
      checkpoints.forEach(cp => {
        steps.push({ type: 'checkpoint', data: cp });
      });

      // Drag & Drop exercise
      const dragDropData = getModuleDragDrop(moduleOrder, courseId);
      if (dragDropData) {
        steps.push({ type: 'dragdrop', data: dragDropData });
      }

      // Fill in the blank
      const fillBlankData = getModuleFillBlanks(moduleOrder, courseId);
      fillBlankData.forEach(fb => {
        steps.push({ type: 'fillblank', data: fb });
      });

      // Scenario
      const scenarioData = getModuleScenario(moduleOrder, courseId);
      if (scenarioData) {
        steps.push({ type: 'scenario', data: scenarioData });
      }
    }

    // KI-generierte Übungen (Fallszenarien + Zuordnungen)
    dbExercises.forEach(ex => {
      if (ex.type === 'scenario') {
        steps.push({ type: 'scenario', data: { scenario: ex.scenario, question: ex.question, choices: ex.choices } });
      } else if (ex.type === 'matching') {
        steps.push({ type: 'matching', data: ex });
      }
    });

    // Audio summary with Dickies Held tip
    if (moduleData?.dickies_tip) {
      steps.push({ 
        type: 'audio', 
        data: { 
          title: 'Zusammenfassung der Dozentin',
          content: moduleData.dickies_tip 
        } 
      });
    }

    return steps;
  };

  const steps = generateLearningSteps();

  if (!exLoaded) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-terra" />
      </div>
    );
  }

  const handleStepComplete = (success) => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    if (success) setScore(prev => prev + 1);
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // All done!
      onComplete?.({ score, total: steps.length });
    }
  };

  const handleSpeak = async (text) => {
    // Zweiter Klick = Stoppen
    if (isPlaying) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
      window.speechSynthesis?.cancel();
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    try {
      if (onSpeak) {
        await onSpeak(text);
        return;
      }
      // Eingebaute Sprachausgabe: OpenAI-TTS über das Backend
      const r = await axios.post(`${EX_API}/tts/base64`, { text: String(text).slice(0, 4000), voice: 'nova' }, authHeader);
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = `data:audio/mpeg;base64,${r.data.audio}`;
      await new Promise((resolve, reject) => {
        audioRef.current.onended = resolve;
        audioRef.current.onerror = reject;
        audioRef.current.play().catch(reject);
      });
    } catch (e) {
      // Fallback: Browser-Sprachausgabe (funktioniert offline/ohne TTS-Dienst)
      try {
        window.speechSynthesis?.cancel();
        const u = new SpeechSynthesisUtterance(String(text).slice(0, 4000));
        u.lang = 'de-DE';
        await new Promise((res) => { u.onend = res; u.onerror = res; window.speechSynthesis.speak(u); });
      } catch { /* keine Sprachausgabe verfügbar */ }
    } finally {
      setIsPlaying(false);
    }
  };

  const currentStepData = steps[currentStep];

  // Progress bar
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="space-y-6">
      {/* Progress Header — Espresso-Zeile */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#23211E' }}>
        <div className="flex items-center justify-between px-5 py-3.5">
          <span className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.24em', color: '#D89232' }}>
            Interaktiv lernen · Schritt {currentStep + 1} von {steps.length}
          </span>
          <span className="flex items-baseline gap-1.5">
            <span style={{ color: '#D89232', fontSize: 11 }}>✳</span>
            <span className="font-display text-lg leading-none" style={{ color: '#D89232' }}>{score}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: 'rgba(245,240,234,0.5)' }}>Punkte</span>
          </span>
        </div>
        <div className="h-[2px] mx-5 mb-3.5 rounded-full overflow-hidden" style={{ backgroundColor: '#2E2B27' }}>
          <div 
            className="h-full transition-all duration-500"
            style={{ width: `${progress}%`, backgroundColor: '#D89232' }}
          />
        </div>
      </div>

      {/* Current Step Content */}
      <div className="min-h-[400px] flex items-center justify-center">
        {currentStepData?.type === 'flashcards' && (
          <FlashCardDeck 
            key={`flashcards-${currentStep}`}
            cards={currentStepData.data.cards}
            onComplete={() => handleStepComplete(true)}
            onSpeak={handleSpeak}
          />
        )}
        
        {currentStepData?.type === 'checkpoint' && (
          <MiniCheckpoint 
            key={`checkpoint-${currentStep}`}
            {...currentStepData.data}
            onComplete={handleStepComplete}
            onSpeak={handleSpeak}
          />
        )}
        
        {currentStepData?.type === 'dragdrop' && (
          <DragDropExercise 
            key={`dragdrop-${currentStep}`}
            {...currentStepData.data}
            onComplete={handleStepComplete}
          />
        )}
        
        {currentStepData?.type === 'fillblank' && (
          <FillInTheBlank 
            key={`fillblank-${currentStep}`}
            {...currentStepData.data}
            onComplete={handleStepComplete}
          />
        )}
        
        {currentStepData?.type === 'scenario' && (
          <ScenarioLearning 
            key={`scenario-${currentStep}`}
            {...currentStepData.data}
            onComplete={handleStepComplete}
          />
        )}
        
        {currentStepData?.type === 'matching' && (
          <MatchingStep
            key={`matching-${currentStep}`}
            {...currentStepData.data}
            onComplete={handleStepComplete}
          />
        )}

        {currentStepData?.type === 'audio' && (
          <AudioSection 
            key={`audio-${currentStep}`}
            {...currentStepData.data}
            onSpeak={handleSpeak}
            isPlaying={isPlaying}
            onComplete={() => handleStepComplete(true)}
          />
        )}
      </div>

      {/* Skip / Navigation */}
      <div className="flex justify-center">
        <button
          onClick={() => handleStepComplete(false)}
          className="text-sm text-sand-500 hover:text-sand-500 transition-colors"
        >
          Überspringen →
        </button>
      </div>
    </div>
  );
};

// Flashcard deck wrapper
const FlashCardDeck = ({ cards, onComplete, onSpeak }) => {
  const [current, setCurrent] = useState(0);

  const handleNext = () => {
    if (current < cards.length - 1) {
      setCurrent(prev => prev + 1);
    } else {
      onComplete();
    }
  };

  return (
    <FlashCard
      front={cards[current].front}
      back={cards[current].back}
      current={current}
      total={cards.length}
      onNext={handleNext}
      onPrev={() => setCurrent(prev => Math.max(0, prev - 1))}
      onSpeak={onSpeak}
    />
  );
};

// ==================== MODULE-SPECIFIC CONTENT ====================
// LG Course (Behandlungspflege LG1/LG2) checkpoints
const lgCheckpoints = {
  1: [
    {
      question: "Welche Qualifikation benötigt man für Behandlungspflege LG1?",
      options: ["Keine Qualifikation", "Grundkurs Behandlungspflege", "Examinierte Pflegekraft", "Ärztliche Zulassung"],
      correctIndex: 1,
      explanation: "Für LG1 Behandlungspflege ist ein Grundkurs Behandlungspflege erforderlich."
    }
  ],
  2: [
    {
      question: "Was gehört zur Behandlungspflege LG1?",
      options: ["Injektionen", "Medikamentengabe", "Wundversorgung", "Infusionen"],
      correctIndex: 1,
      explanation: "Die Medikamentengabe (z.B. Tabletten richten und verabreichen) gehört zur LG1."
    }
  ],
  3: [
    {
      question: "Wer verordnet Behandlungspflege?",
      options: ["Pflegedienst", "Der Arzt", "Die Krankenkasse", "Der Patient selbst"],
      correctIndex: 1,
      explanation: "Behandlungspflege wird vom Arzt verordnet und von der Krankenkasse genehmigt."
    }
  ],
  4: [
    {
      question: "Was ist bei der Medikamentengabe besonders wichtig?",
      options: ["Schnelligkeit", "Die 5-R-Regel", "Eigenständiges Handeln", "Verzicht auf Dokumentation"],
      correctIndex: 1,
      explanation: "Die 5-R-Regel: Richtiger Patient, Richtiges Medikament, Richtige Dosis, Richtige Zeit, Richtige Verabreichung."
    }
  ],
  5: [
    {
      question: "Wie oft muss Behandlungspflege dokumentiert werden?",
      options: ["Einmal pro Woche", "Bei jeder Durchführung", "Nur bei Problemen", "Monatlich"],
      correctIndex: 1,
      explanation: "Jede Behandlungspflege-Maßnahme muss bei jeder Durchführung dokumentiert werden."
    }
  ],
  6: [
    {
      question: "Was gehört zur Behandlungspflege LG2?",
      options: ["Tabletten richten", "Kompressionsstrümpfe anziehen", "Subkutane Injektionen", "Körperpflege"],
      correctIndex: 2,
      explanation: "Subkutane Injektionen (z.B. Insulin) gehören zur LG2 Behandlungspflege."
    }
  ],
  7: [
    {
      question: "Was ist bei der Insulingabe zu beachten?",
      options: ["Immer in den Bauch spritzen", "Injektionsstellen wechseln", "Nur morgens geben", "Ohne Arztanordnung möglich"],
      correctIndex: 1,
      explanation: "Bei der Insulingabe müssen die Injektionsstellen regelmäßig gewechselt werden (Lipodystrophie-Prophylaxe)."
    }
  ],
  8: [
    {
      question: "Wie lange ist eine Verordnung für Behandlungspflege gültig?",
      options: ["1 Woche", "14 Tage bei Erstverordnung", "1 Jahr", "Unbegrenzt"],
      correctIndex: 1,
      explanation: "Die Erstverordnung für Behandlungspflege ist 14 Tage gültig, Folgeverordnungen können länger sein."
    }
  ]
};

// Wundmanagement checkpoints (default)
const wundCheckpoints = {
  1: [
    {
      question: "Ab welchem Zeitraum gilt eine Wunde als chronisch?",
      options: ["2 Wochen", "4-6 Wochen", "3 Monate", "6 Monate"],
      correctIndex: 1,
      explanation: "Eine Wunde gilt als chronisch, wenn sie trotz korrekter Behandlung innerhalb von 4-6 Wochen keine Heilungstendenz zeigt."
    },
    {
      question: "Welche Wundart ist im Pflegesetting am häufigsten?",
      options: ["Ulcus cruris", "Diabetisches Fußsyndrom", "Dekubitus", "Verbrennung"],
      correctIndex: 2,
      explanation: "Dekubitus macht mit 35% den größten Anteil chronischer Wunden im Pflegesetting aus."
    }
  ],
  2: [
    {
      question: "Was passiert bei fehlender Dokumentation laut § 630h BGB?",
      options: ["Nichts", "Beweislastumkehr", "Automatische Strafe", "Verwarnung"],
      correctIndex: 1,
      explanation: "Bei Dokumentationsmängeln tritt Beweislastumkehr ein - Sie müssen beweisen, dass kein Fehler vorlag!"
    }
  ],
  3: [
    {
      question: "Wofür steht das 'A' im RACI-Modell?",
      options: ["Active", "Approved", "Accountable", "Available"],
      correctIndex: 2,
      explanation: "A = Accountable - die Person, die rechenschaftspflichtig ist und die Endergebnisverantwortung trägt."
    }
  ],
  4: [
    {
      question: "Wie viel höher ist die MD-Erfolgsquote bei regelmäßigen internen Audits?",
      options: ["10%", "20%", "30%", "50%"],
      correctIndex: 2,
      explanation: "Einrichtungen mit regelmäßigen internen Audits haben 30% höhere MD-Prüfungsquoten ohne Beanstandungen."
    }
  ],
  5: [
    {
      question: "Wie viel kostet die Behandlung einer chronischen Wunde durchschnittlich pro Jahr?",
      options: ["2.000-3.000 €", "5.000-6.000 €", "8.000-9.500 €", "15.000 €"],
      correctIndex: 2,
      explanation: "Die durchschnittlichen Behandlungskosten belaufen sich auf 8.000-9.500 € jährlich pro Patient."
    }
  ],
  6: [
    {
      question: "Um wie viel Prozent haben Teams mit offener Fehlerkultur weniger Komplikationen?",
      options: ["10%", "20%", "30%", "50%"],
      correctIndex: 2,
      explanation: "Teams mit offener Fehlerkultur haben bis zu 30% weniger Komplikationen."
    }
  ],
  7: [
    {
      question: "Wo dürfen Wundfotos NIEMALS gespeichert werden?",
      options: ["Patientenakte", "Digitales System", "Private Smartphones", "Server"],
      correctIndex: 2,
      explanation: "Wundfotos auf privaten Smartphones verstoßen gegen die DSGVO!"
    }
  ],
  8: [
    {
      question: "Nach wie vielen Monaten erreicht ein strukturiertes Wundmanagement den Break-even?",
      options: ["3 Monate", "6 Monate", "9 Monate", "12 Monate"],
      correctIndex: 2,
      explanation: "Ein strukturiertes Wundmanagement-Programm erreicht nach etwa 9 Monaten den Break-even."
    }
  ]
};

const getModuleCheckpoints = (moduleOrder, courseId) => {
  // Select checkpoints based on course
  if (courseId === 'course-behandlungspflege-lg') {
    return lgCheckpoints[moduleOrder] || [];
  }
  if (courseId === 'course-pdl-weiterbildung-teil1') {
    return pdlTeil1Checkpoints[moduleOrder] || [];
  }
  if (courseId === 'course-pdl-weiterbildung-teil2') {
    return pdlTeil2Checkpoints[moduleOrder] || [];
  }
  if (courseId === 'course-weiterbildung-wundexperte') {
    return wundexperteCheckpoints[moduleOrder] || [];
  }
  if (courseId === 'course-wundmanagement-pflegefachkraefte') {
    return pflegefachkraefteCheckpoints[moduleOrder] || [];
  }
  // Jahresfortbildung Kurse
  if (courseId === 'course-hygiene-infektion') {
    return hygieneCheckpoints[moduleOrder] || [];
  }
  if (courseId === 'course-haftung-delegation') {
    return haftungCheckpoints[moduleOrder] || [];
  }
  if (courseId === 'course-gewaltpraevention') {
    return gewaltCheckpoints[moduleOrder] || [];
  }
  if (courseId === 'course-kommunikation') {
    return kommunikationCheckpoints[moduleOrder] || [];
  }
  if (courseId === 'course-rezertifizierung-43b') {
    return rezert43bCheckpoints[moduleOrder] || [];
  }
  // Pflegeberater Kurs
  if (courseId === 'course-pflegeberater') {
    return pflegeberaterCheckpoints[moduleOrder] || [];
  }
  // Praxisanleiter Kurs
  if (courseId === 'course-praxisanleiter') {
    return praxisanleiterCheckpoints[moduleOrder] || [];
  }
  // Palliativpflege Kurs
  if (courseId === 'course-palliativpflege') {
    return palliativpflegeCheckpoints[moduleOrder] || [];
  }
  // Pflege-KI Führerschein
  if (courseId === 'course-pflege-ki') {
    return pflegeKiCheckpoints[moduleOrder] || [];
  }
  // Wund-Inhalte NUR für Wund-Kurse — unbekannte Kurse bekommen keine falschen Inhalte
  return (courseId || '').includes('wund') ? (wundCheckpoints[moduleOrder] || []) : [];
};

// Pflege-KI Führerschein Checkpoints (aus dem Praxis-Handbuch)
const pflegeKiCheckpoints = {
  1: [{ question: "Was macht die Pflege-KI für dich?", options: ["Sie pflegt den Patienten", "Sie schreibt aus deinen Beobachtungen fachliche Texte", "Sie stellt Diagnosen", "Sie ersetzt die PDL"], correctIndex: 1, explanation: "Die KI schreibt — du prüfst, entscheidest und gibst frei." }],
  2: [{ question: "Wie viele Sektionen hat die Patientenmappe?", options: ["6", "12", "26", "3"], correctIndex: 1, explanation: "Die digitale Akte hat 12 Sektionen — von Stammdaten bis Team-Chat." }],
  3: [{ question: "Wie diktierst du richtig?", options: ["In perfekter Schriftsprache", "Kurze Sätze mit Fakten — die KI glättet", "Möglichst lange Sätze", "Nur Diagnosen nennen"], correctIndex: 1, explanation: "Kurz und konkret sprechen — sag, was du siehst, nicht, was du vermutest." }],
  4: [{ question: "Woraus schreibt die KI die SIS-Themenfelder?", options: ["Aus dem Internet", "Nur aus deinen Angaben", "Aus fremden Akten", "Aus alten MD-Berichten"], correctIndex: 1, explanation: "Halluzinationsschutz: Die KI nutzt ausschließlich deine Eingaben." }],
  5: [{ question: "Was braucht jedes rote Risiko in der Risikomatrix?", options: ["Einen neuen Klienten", "Mindestens eine passende Maßnahme", "Einen Arzttermin", "Nichts"], correctIndex: 1, explanation: "Rotes Risiko ohne Maßnahme ist eine Lücke, die dem Prüfer sofort auffällt." }],
  6: [{ question: "Wer entscheidet am Ende über den Pflegegrad?", options: ["Die KI", "Die PDL", "Der Medizinische Dienst (MD)", "Die Angehörigen"], correctIndex: 2, explanation: "Die NBA-Analyse zeigt nur eine Tendenz — die Entscheidung trifft der MD." }],
  7: [{ question: "Was tut der MD-Simulator?", options: ["Er ersetzt die Prüfung", "Er findet Widersprüche in der Doku — mit Fundstelle und Lösungsvorschlag", "Er ruft den MD an", "Er druckt die Akte"], correctIndex: 1, explanation: "Die Generalprobe vor der echten Prüfung: üben, bevor es zählt." }],
  8: [{ question: "Was bedeutet die digitale Unterschrift bei der Freigabe?", options: ["Nur eine Formsache", "Sie ist rechtlich deine Verantwortung — wie auf Papier", "Der Anbieter haftet", "Sie ist optional"], correctIndex: 1, explanation: "Freigabe = Verantwortung. Die Signatur-Historie hält alles revisionssicher fest." }],
};

// Pflegeberater nach §7a SGB XI Checkpoints
const pflegeberaterCheckpoints = {
  1: [
    {
      question: "In welchem Paragraphen ist der Anspruch auf Pflegeberatung geregelt?",
      options: ["§7 SGB XI", "§7a SGB XI", "§14 SGB XI", "§37 SGB XI"],
      correctIndex: 1,
      explanation: "Der Anspruch auf Pflegeberatung ist in §7a SGB XI geregelt."
    },
    {
      question: "Wer hat Anspruch auf Pflegeberatung?",
      options: ["Nur Pflegebedürftige mit Pflegegrad 3-5", "Alle Versicherten mit Pflegebedarf", "Nur Angehörige", "Nur stationär Versorgte"],
      correctIndex: 1,
      explanation: "Alle Versicherten mit Pflegebedarf sowie deren Angehörige haben Anspruch auf Pflegeberatung."
    }
  ],
  2: [
    {
      question: "Wie viele Pflegegrade gibt es seit 2017?",
      options: ["3 Pflegegrade", "4 Pflegegrade", "5 Pflegegrade", "6 Pflegegrade"],
      correctIndex: 2,
      explanation: "Seit 2017 gibt es 5 Pflegegrade, die den Grad der Selbstständigkeit abbilden."
    },
    {
      question: "Was wird beim NBA (Neues Begutachtungsassessment) bewertet?",
      options: ["Nur körperliche Einschränkungen", "Nur kognitive Fähigkeiten", "Grad der Selbstständigkeit in 6 Modulen", "Nur Mobilität"],
      correctIndex: 2,
      explanation: "Das NBA bewertet den Grad der Selbstständigkeit in 6 Modulen: Mobilität, kognitive Fähigkeiten, Verhaltensweisen, Selbstversorgung, Krankheitsbedingte Anforderungen und Alltagsgestaltung."
    }
  ],
  3: [
    {
      question: "Was ist das Ziel des Case Managements in der Pflegeberatung?",
      options: ["Kostenreduzierung", "Koordinierte und bedarfsgerechte Versorgung", "Nur Dokumentation", "Nur Kontrolle"],
      correctIndex: 1,
      explanation: "Case Management zielt auf eine koordinierte, bedarfsgerechte Versorgung unter Einbeziehung aller relevanten Akteure."
    },
    {
      question: "Was ist ein Versorgungsplan?",
      options: ["Nur ein Formular", "Individueller Plan mit Zielen und Maßnahmen", "Nur für Pflegeheime", "Nur für Ärzte"],
      correctIndex: 1,
      explanation: "Der Versorgungsplan dokumentiert individuelle Ziele, Maßnahmen und Verantwortlichkeiten für die Pflege und Betreuung."
    }
  ],
  4: [
    {
      question: "Welche Gesprächstechnik fördert das Verständnis in der Beratung?",
      options: ["Monolog halten", "Aktives Zuhören", "Schnell unterbrechen", "Nur schriftlich kommunizieren"],
      correctIndex: 1,
      explanation: "Aktives Zuhören fördert Verständnis und Vertrauen in der Beratungssituation."
    },
    {
      question: "Was bedeutet Empathie in der Pflegeberatung?",
      options: ["Mitleid zeigen", "Sich in die Lage des Gegenübers versetzen", "Distanz wahren", "Eigene Meinung durchsetzen"],
      correctIndex: 1,
      explanation: "Empathie bedeutet, sich in die Gefühle und Perspektive des Ratsuchenden hineinzuversetzen."
    }
  ],
  5: [
    {
      question: "Welche Erkrankung betrifft besonders viele Pflegebedürftige?",
      options: ["Grippe", "Demenz", "Allergie", "Migräne"],
      correctIndex: 1,
      explanation: "Demenz betrifft einen großen Teil der Pflegebedürftigen und erfordert besondere Beratungskompetenz."
    },
    {
      question: "Was ist bei der Beratung von Menschen mit Mehrfacherkrankungen wichtig?",
      options: ["Nur eine Erkrankung berücksichtigen", "Ganzheitliche Betrachtung", "Nur Medikamente besprechen", "Beratung ablehnen"],
      correctIndex: 1,
      explanation: "Bei Multimorbidität ist eine ganzheitliche Betrachtung aller Erkrankungen und deren Wechselwirkungen wichtig."
    }
  ],
  6: [
    {
      question: "Was ist Palliative Care?",
      options: ["Heilende Behandlung", "Lebensqualitätserhaltende Versorgung am Lebensende", "Nur Schmerztherapie", "Nur für Krebspatienten"],
      correctIndex: 1,
      explanation: "Palliative Care umfasst die ganzheitliche Betreuung zur Erhaltung der Lebensqualität in der letzten Lebensphase."
    },
    {
      question: "Was ist eine Patientenverfügung?",
      options: ["Ärztliche Anordnung", "Vorsorgedokument für Behandlungswünsche", "Pflegevertrag", "Kostenübernahmeerklärung"],
      correctIndex: 1,
      explanation: "Die Patientenverfügung dokumentiert die Behandlungswünsche für den Fall der Entscheidungsunfähigkeit."
    }
  ],
  7: [
    {
      question: "Was ist ein wichtiger Netzwerkpartner in der Pflegeberatung?",
      options: ["Nur die Pflegekasse", "Vielfältige Akteure wie Ärzte, Pflegedienste, Selbsthilfe", "Nur Angehörige", "Nur Krankenhäuser"],
      correctIndex: 1,
      explanation: "Effektive Pflegeberatung erfordert die Vernetzung mit verschiedenen Akteuren im Gesundheits- und Sozialwesen."
    },
    {
      question: "Warum ist Schnittstellenmanagement wichtig?",
      options: ["Nur für Verwaltung", "Für nahtlose Übergänge zwischen Versorgungsbereichen", "Nur Kostengründe", "Ist nicht wichtig"],
      correctIndex: 1,
      explanation: "Gutes Schnittstellenmanagement verhindert Versorgungsbrüche bei Übergängen, z.B. vom Krankenhaus nach Hause."
    }
  ],
  8: [
    {
      question: "Was ist bei der Dokumentation in der Pflegeberatung wichtig?",
      options: ["Nur Notizen machen", "Nachvollziehbare, strukturierte Dokumentation", "Mündlich reicht aus", "Nur am Ende dokumentieren"],
      correctIndex: 1,
      explanation: "Eine strukturierte Dokumentation sichert die Qualität und Nachvollziehbarkeit der Beratung."
    },
    {
      question: "Warum ist Qualitätssicherung wichtig?",
      options: ["Nur für Kontrollen", "Kontinuierliche Verbesserung der Beratungsqualität", "Nur gesetzliche Pflicht", "Ist nicht notwendig"],
      correctIndex: 1,
      explanation: "Qualitätssicherung dient der kontinuierlichen Verbesserung und Sicherstellung hochwertiger Beratung."
    }
  ],
  9: [
    {
      question: "Was sind ethische Grundsätze in der Pflegeberatung?",
      options: ["Nur Gewinnorientierung", "Autonomie, Würde, Selbstbestimmung", "Nur Effizienz", "Nur rechtliche Vorgaben"],
      correctIndex: 1,
      explanation: "Ethische Grundsätze wie Autonomie, Würde und Selbstbestimmung sind Fundament professioneller Beratung."
    },
    {
      question: "Was bedeutet Selbstbestimmung in der Pflege?",
      options: ["Der Berater entscheidet", "Der Pflegebedürftige entscheidet über sein Leben", "Die Kasse entscheidet", "Das Pflegeheim entscheidet"],
      correctIndex: 1,
      explanation: "Selbstbestimmung bedeutet, dass der Pflegebedürftige so weit wie möglich eigene Entscheidungen trifft."
    }
  ],
  10: [
    {
      question: "Was ist das Ziel der Fallarbeit in der Pflegeberater-Ausbildung?",
      options: ["Nur Theorie lernen", "Praktische Anwendung des Gelernten", "Prüfungsvorbereitung", "Nur Dokumentation üben"],
      correctIndex: 1,
      explanation: "Fallarbeit ermöglicht die praktische Anwendung und Vertiefung theoretischer Kenntnisse."
    },
    {
      question: "Welche Kompetenz ist für Pflegeberater besonders wichtig?",
      options: ["Nur Fachwissen", "Beratungskompetenz und Empathie", "Nur Verwaltungskenntnisse", "Nur medizinisches Wissen"],
      correctIndex: 1,
      explanation: "Neben Fachwissen sind Beratungskompetenz, Empathie und Kommunikationsfähigkeit zentrale Kompetenzen."
    }
  ]
};

// Praxisanleiter Checkpoints (Grundlagen für alle Module)
const praxisanleiterCheckpoints = {
  1: [
    {
      question: "Was ist die Hauptaufgabe eines Praxisanleiters?",
      options: ["Nur Dienstplanung", "Anleitung und Begleitung von Auszubildenden", "Nur Dokumentation", "Nur Medikamentenvergabe"],
      correctIndex: 1,
      explanation: "Praxisanleiter sind für die praktische Anleitung und pädagogische Begleitung von Auszubildenden verantwortlich."
    }
  ]
};

// Palliativpflege Checkpoints (Grundlagen für alle Module)
const palliativpflegeCheckpoints = {
  1: [
    {
      question: "Was ist das Hauptziel der Palliativpflege?",
      options: ["Heilung", "Lebensqualität erhalten und verbessern", "Lebensverlängerung", "Nur Schmerztherapie"],
      correctIndex: 1,
      explanation: "Palliativpflege zielt auf die Erhaltung und Verbesserung der Lebensqualität bei schwerer Krankheit."
    }
  ]
};

// PDL Teil 1 Checkpoints
const pdlTeil1Checkpoints = {
  1: [
    {
      question: "Was ist die Hauptaufgabe einer PDL?",
      options: ["Nur Dienstplanung", "Gesamtverantwortung für die Pflege", "Nur Medikamentenvergabe", "Nur Dokumentation"],
      correctIndex: 1,
      explanation: "Die PDL trägt die Gesamtverantwortung für die pflegerische Versorgung der Einrichtung."
    }
  ],
  2: [
    {
      question: "Welches Gesetz regelt die Pflegequalität?",
      options: ["SGB V", "SGB XI", "BGB", "StGB"],
      correctIndex: 1,
      explanation: "Das SGB XI (Soziale Pflegeversicherung) regelt die Pflegequalität in Deutschland."
    }
  ],
  3: [
    {
      question: "Was bedeutet Delegation in der Pflege?",
      options: ["Arbeit ablehnen", "Aufgaben übertragen", "Urlaub nehmen", "Kündigen"],
      correctIndex: 1,
      explanation: "Delegation bedeutet die Übertragung von Aufgaben an qualifizierte Mitarbeiter unter Beibehaltung der Verantwortung."
    }
  ]
};

// PDL Teil 2 Checkpoints
const pdlTeil2Checkpoints = {
  1: [
    {
      question: "Was ist der wichtigste Grundsatz der Pflegedokumentation?",
      options: ["Schnelligkeit", "Vollständigkeit und Nachvollziehbarkeit", "Kürze", "Schönschrift"],
      correctIndex: 1,
      explanation: "Die Dokumentation muss vollständig und nachvollziehbar sein - was nicht dokumentiert ist, gilt als nicht durchgeführt."
    }
  ],
  2: [
    {
      question: "Wie lange müssen Pflegedokumentationen aufbewahrt werden?",
      options: ["5 Jahre", "10 Jahre", "15 Jahre", "30 Jahre"],
      correctIndex: 1,
      explanation: "Die gesetzliche Aufbewahrungsfrist für Pflegedokumentationen beträgt 10 Jahre."
    }
  ],
  3: [
    {
      question: "Was ist bei der Abrechnung mit Pflegekassen wichtig?",
      options: ["Schnelle Bearbeitung", "Korrekte Leistungsnachweise", "Hohe Preise", "Wenig Dokumentation"],
      correctIndex: 1,
      explanation: "Korrekte und vollständige Leistungsnachweise sind für die Abrechnung mit Pflegekassen unerlässlich."
    }
  ]
};

// Wundexperte Checkpoints
const wundexperteCheckpoints = {
  1: [
    {
      question: "Aus wie vielen Schichten besteht die menschliche Haut?",
      options: ["2 Schichten", "3 Schichten", "4 Schichten", "5 Schichten"],
      correctIndex: 1,
      explanation: "Die Haut besteht aus 3 Schichten: Epidermis (Oberhaut), Dermis (Lederhaut) und Subcutis (Unterhaut)."
    }
  ],
  2: [
    {
      question: "Wie lange dauert die Granulationsphase der Wundheilung?",
      options: ["1-4 Tage", "4-21 Tage", "21-42 Tage", "Über 6 Wochen"],
      correctIndex: 1,
      explanation: "Die Granulationsphase dauert vom 4. bis zum 21. Tag - hier bildet sich neues Gewebe."
    }
  ],
  3: [
    {
      question: "Wofür steht das M im MEASURE-Konzept?",
      options: ["Material", "Measure (Messen)", "Medikament", "Mobilisation"],
      correctIndex: 1,
      explanation: "M steht für Measure - das Messen von Länge, Breite und Tiefe der Wunde."
    }
  ],
  4: [
    {
      question: "Ab wann gilt eine Wunde als chronisch?",
      options: ["Nach 2 Wochen", "Nach 4-6 Wochen", "Nach 3 Monaten", "Nach 6 Monaten"],
      correctIndex: 1,
      explanation: "Eine Wunde gilt als chronisch, wenn sie nach 4-6 Wochen keine Heilungstendenz zeigt."
    }
  ],
  5: [
    {
      question: "Welche Wundauflage eignet sich für stark exsudierende Wunden?",
      options: ["Hydrogel", "Hydrokolloide", "Alginate", "Folienverbände"],
      correctIndex: 2,
      explanation: "Alginate haben die 20-fache Saugfähigkeit und sind ideal für stark nässende Wunden."
    }
  ],
  6: [
    {
      question: "Was muss VOR jeder Kompressionstherapie gemessen werden?",
      options: ["Blutdruck", "ABI (Knöchel-Arm-Index)", "Blutzucker", "Temperatur"],
      correctIndex: 1,
      explanation: "Der ABI muss vor jeder Kompression gemessen werden - bei ABI unter 0,5 ist Kompression kontraindiziert!"
    }
  ],
  7: [
    {
      question: "Was ist die wichtigste Maßnahme beim diabetischen Fußsyndrom?",
      options: ["Antibiotika", "Druckentlastung", "Kompression", "Bettruhe"],
      correctIndex: 1,
      explanation: "Druckentlastung ist die wichtigste Maßnahme - ohne sie heilt kein neuropathisches Ulkus."
    }
  ],
  8: [
    {
      question: "Welche Dekubitus-Kategorie zeigt freiliegenden Knochen?",
      options: ["Kategorie I", "Kategorie II", "Kategorie III", "Kategorie IV"],
      correctIndex: 3,
      explanation: "Kategorie IV zeigt vollständigen Gewebeverlust mit freiliegendem Knochen, Sehnen oder Muskeln."
    }
  ],
  9: [
    {
      question: "Wie viel Prozent der chronischen Wunden haben Biofilm?",
      options: ["20%", "40%", "60%", "80%"],
      correctIndex: 3,
      explanation: "80% aller chronischen Wunden haben Biofilm - daher ist regelmäßiges Débridement wichtig."
    }
  ],
  10: [
    {
      question: "Wann sollten Analgetika vor dem Verbandwechsel gegeben werden?",
      options: ["5 Minuten vorher", "30-60 Minuten vorher", "Direkt beim Verbandwechsel", "Danach"],
      correctIndex: 1,
      explanation: "Analgetika sollten 30-60 Minuten vor dem Verbandwechsel gegeben werden für optimale Wirkung."
    }
  ],
  11: [
    {
      question: "Was regelt die DSGVO in Bezug auf Wundfotografie?",
      options: ["Bildqualität", "Datenschutz und Einwilligung", "Kameratyp", "Belichtung"],
      correctIndex: 1,
      explanation: "Die DSGVO regelt den Schutz von Patientendaten - informierte Einwilligung ist vor jeder Wundfotografie Pflicht."
    }
  ],
  12: [
    {
      question: "Wofür steht PDCA im Qualitätsmanagement?",
      options: ["Patient-Diagnose-Check-Analyse", "Plan-Do-Check-Act", "Pflege-Dokumentation-Controlling-Audit", "Prüfung-Durchführung-Kontrolle-Abschluss"],
      correctIndex: 1,
      explanation: "PDCA steht für Plan-Do-Check-Act - der kontinuierliche Verbesserungszyklus."
    }
  ],
  13: [
    {
      question: "Was ist der Goldstandard bei der Therapie des venösen Ulcus cruris?",
      options: ["Antibiotika", "Kompressionstherapie", "Operation", "Bettruhe"],
      correctIndex: 1,
      explanation: "Die Kompressionstherapie ist der Goldstandard beim venösen Ulkus - ohne Kompression keine Heilung!"
    }
  ]
};

// Pflegefachkräfte Checkpoints
const pflegefachkraefteCheckpoints = {
  1: [
    {
      question: "Was ist das Ziel der Wundbehandlung durch Pflegefachkräfte?",
      options: ["Schnelle Heilung", "Optimale Wundheilung unter Vermeidung von Komplikationen", "Kostenreduktion", "Weniger Arztbesuche"],
      correctIndex: 1,
      explanation: "Das Ziel ist die optimale Wundheilung unter Vermeidung von Komplikationen wie Infektionen."
    }
  ],
  2: [
    {
      question: "Welche Wundversorgung dürfen Pflegefachkräfte selbstständig durchführen?",
      options: ["Alle Wundversorgungen", "Nur Verbandwechsel nach ärztlicher Anordnung", "Keine Wundversorgung", "Nur bei akuten Wunden"],
      correctIndex: 1,
      explanation: "Pflegefachkräfte führen Verbandwechsel nach ärztlicher Anordnung und Expertenstandards durch."
    }
  ],
  3: [
    {
      question: "Was gehört zur Wunddokumentation durch Pflegefachkräfte?",
      options: ["Nur Foto", "Nur Größe", "Wundstatus, Therapie, Verlauf", "Nur Datum"],
      correctIndex: 2,
      explanation: "Die Dokumentation umfasst Wundstatus, durchgeführte Therapie und den Heilungsverlauf."
    }
  ]
};

// ==================== JAHRESFORTBILDUNG KURSE ====================

// Hygiene und Infektionsprävention
const hygieneCheckpoints = {
  1: [
    {
      question: "Wie lange sollte die hygienische Händedesinfektion mindestens dauern?",
      options: ["10 Sekunden", "30 Sekunden", "1 Minute", "2 Minuten"],
      correctIndex: 1,
      explanation: "Die hygienische Händedesinfektion muss mindestens 30 Sekunden dauern."
    },
    {
      question: "Wann ist eine Händedesinfektion erforderlich?",
      options: ["Nur vor dem Essen", "Vor und nach jedem Patientenkontakt", "Einmal pro Schicht", "Nur bei sichtbarer Verschmutzung"],
      correctIndex: 1,
      explanation: "Die 5 Momente der Händehygiene: Vor Patientenkontakt, vor aseptischen Tätigkeiten, nach Kontakt mit Körperflüssigkeiten, nach Patientenkontakt, nach Kontakt mit der Patientenumgebung."
    }
  ],
  2: [
    {
      question: "Was bedeutet MRSA?",
      options: ["Methicillin-resistenter Staphylococcus aureus", "Multi-resistente Streptokokken-Art", "Medizinisch relevante Sepsis-Anzeichen", "Minimale Resistenz gegen Antibiotika"],
      correctIndex: 0,
      explanation: "MRSA = Methicillin-resistenter Staphylococcus aureus, ein multiresistenter Keim."
    }
  ],
  3: [
    {
      question: "Welche Schutzausrüstung ist bei Kontakt mit infektiösem Material erforderlich?",
      options: ["Nur Handschuhe", "Handschuhe und Kittel", "Handschuhe, Kittel und ggf. Maske", "Keine besondere Ausrüstung"],
      correctIndex: 2,
      explanation: "Je nach Infektionsrisiko sind Handschuhe, Schutzkittel und ggf. Mund-Nasen-Schutz erforderlich."
    }
  ]
};

// Haftung und Delegation
const haftungCheckpoints = {
  1: [
    {
      question: "Was bedeutet Delegation in der Pflege?",
      options: ["Arbeit ablehnen", "Aufgaben unter Beibehaltung der Verantwortung übertragen", "Verantwortung abgeben", "Kollegen helfen"],
      correctIndex: 1,
      explanation: "Delegation bedeutet die Übertragung von Aufgaben an qualifizierte Mitarbeiter - die Verantwortung bleibt beim Delegierenden."
    },
    {
      question: "Wer haftet bei einem Behandlungsfehler durch delegierte Aufgaben?",
      options: ["Nur der Ausführende", "Nur der Delegierende", "Beide können haften", "Niemand"],
      correctIndex: 2,
      explanation: "Sowohl der Delegierende (Auswahl- und Überwachungspflicht) als auch der Ausführende (Durchführungsverantwortung) können haften."
    }
  ],
  2: [
    {
      question: "Was ist die Voraussetzung für eine wirksame Delegation?",
      options: ["Schriftliche Anweisung", "Qualifikation des Mitarbeiters", "Genehmigung der PDL", "Einverständnis des Patienten"],
      correctIndex: 1,
      explanation: "Der Mitarbeiter muss für die delegierte Aufgabe qualifiziert sein."
    }
  ],
  3: [
    {
      question: "Was ist die Dokumentationspflicht bei delegierten Aufgaben?",
      options: ["Keine Dokumentation nötig", "Nur bei Problemen dokumentieren", "Jede delegierte Maßnahme dokumentieren", "Nur mündlich berichten"],
      correctIndex: 2,
      explanation: "Jede delegierte und durchgeführte Maßnahme muss dokumentiert werden."
    }
  ]
};

// Gewaltprävention
const gewaltCheckpoints = {
  1: [
    {
      question: "Was gilt als Gewalt in der Pflege?",
      options: ["Nur körperliche Gewalt", "Körperliche, psychische und strukturelle Gewalt", "Nur absichtliche Handlungen", "Nur strafbare Handlungen"],
      correctIndex: 1,
      explanation: "Gewalt umfasst körperliche, psychische und strukturelle Formen - auch Vernachlässigung ist Gewalt."
    },
    {
      question: "Was ist ein Beispiel für strukturelle Gewalt?",
      options: ["Schlagen", "Beschimpfen", "Unzureichende Personalbesetzung", "Fixierung"],
      correctIndex: 2,
      explanation: "Strukturelle Gewalt entsteht durch Rahmenbedingungen wie Personalmangel, Zeitdruck oder fehlende Ressourcen."
    }
  ],
  2: [
    {
      question: "Was tun Sie, wenn Sie Gewalt beobachten?",
      options: ["Wegschauen", "Sofort einschreiten und dokumentieren", "Später melden", "Mit dem Täter reden"],
      correctIndex: 1,
      explanation: "Bei beobachteter Gewalt: Sofort einschreiten, Situation dokumentieren und Vorgesetzten informieren."
    }
  ],
  3: [
    {
      question: "Welche Maßnahme ist KEINE Gewaltprävention?",
      options: ["Deeskalationstraining", "Ausreichend Personal", "Fixierung als Routine", "Supervision"],
      correctIndex: 2,
      explanation: "Routinemäßige Fixierung ist selbst eine Form von Gewalt, keine Prävention."
    }
  ]
};

// Kommunikation
const kommunikationCheckpoints = {
  1: [
    {
      question: "Was ist aktives Zuhören?",
      options: ["Unterbrechen und eigene Meinung sagen", "Aufmerksam zuhören, nachfragen, zusammenfassen", "Nur schweigen", "Ratschläge geben"],
      correctIndex: 1,
      explanation: "Aktives Zuhören bedeutet aufmerksam zuhören, nachfragen und das Gehörte zusammenfassen."
    },
    {
      question: "Was sind Ich-Botschaften?",
      options: ["Von sich selbst reden", "Eigene Gefühle und Wahrnehmungen ausdrücken", "Den anderen kritisieren", "Befehle erteilen"],
      correctIndex: 1,
      explanation: "Ich-Botschaften drücken eigene Gefühle und Wahrnehmungen aus, ohne den anderen anzugreifen."
    }
  ],
  2: [
    {
      question: "Wie kommunizieren Sie mit dementen Menschen?",
      options: ["Schnell und laut sprechen", "Langsam, einfach und mit Blickkontakt", "Komplexe Sätze verwenden", "Ignorieren wenn keine Antwort kommt"],
      correctIndex: 1,
      explanation: "Bei Demenz: Langsam sprechen, einfache Sätze, Blickkontakt, Geduld zeigen."
    }
  ],
  3: [
    {
      question: "Was ist bei der Kommunikation mit Angehörigen wichtig?",
      options: ["Nur auf Anfrage informieren", "Regelmäßig, transparent und wertschätzend kommunizieren", "Fachsprache verwenden", "Probleme verschweigen"],
      correctIndex: 1,
      explanation: "Angehörige brauchen regelmäßige, transparente und wertschätzende Kommunikation."
    }
  ]
};

// Rezertifizierung 43b Betreuungskräfte
const rezert43bCheckpoints = {
  1: [
    {
      question: "Was ist die Hauptaufgabe von Betreuungskräften nach §43b?",
      options: ["Pflegerische Versorgung", "Aktivierung und Betreuung", "Medikamentengabe", "Hauswirtschaft"],
      correctIndex: 1,
      explanation: "Betreuungskräfte nach §43b sind für Aktivierung und Betreuung zuständig, nicht für Pflege."
    }
  ],
  2: [
    {
      question: "Wie oft muss die Fortbildung für Betreuungskräfte erfolgen?",
      options: ["Alle 6 Monate", "Jährlich mindestens 16 Stunden", "Alle 2 Jahre", "Einmalig"],
      correctIndex: 1,
      explanation: "Betreuungskräfte müssen jährlich mindestens 16 Stunden Fortbildung nachweisen."
    }
  ],
  3: [
    {
      question: "Welche Tätigkeit gehört NICHT zu den Aufgaben einer Betreuungskraft?",
      options: ["Vorlesen", "Spiele anbieten", "Injektionen geben", "Gespräche führen"],
      correctIndex: 2,
      explanation: "Injektionen sind pflegerische/medizinische Tätigkeiten - nicht Aufgabe von Betreuungskräften."
    }
  ]
};

// LG Course Drag & Drop
const lgDragDrops = {
  1: {
    title: "Behandlungspflege-Stufen zuordnen",
    items: [
      { id: 1, term: "LG1", definition: "Medikamentengabe, Kompressionsstrümpfe" },
      { id: 2, term: "LG2", definition: "Injektionen, Blutzuckermessung" },
      { id: 3, term: "Grundpflege", definition: "Körperpflege, Ernährung" }
    ]
  },
  2: {
    title: "5-R-Regel der Medikamentengabe",
    items: [
      { id: 1, term: "Richtiger Patient", definition: "Identität prüfen" },
      { id: 2, term: "Richtiges Medikament", definition: "Präparat kontrollieren" },
      { id: 3, term: "Richtige Dosis", definition: "Menge überprüfen" },
      { id: 4, term: "Richtige Zeit", definition: "Einnahmezeit beachten" }
    ]
  }
};

const wundDragDrops = {
  1: {
    title: "Wundheilungsphasen zuordnen",
    items: [
      { id: 1, term: "Exsudationsphase", definition: "Reinigung und Entzündung" },
      { id: 2, term: "Granulationsphase", definition: "Gewebeneubildung" },
      { id: 3, term: "Epithelisierungsphase", definition: "Narbenbildung" }
    ]
  },
  2: {
    title: "Rechtsbegriffe zuordnen",
    items: [
      { id: 1, term: "§ 630h BGB", definition: "Beweislastumkehr" },
      { id: 2, term: "§ 630f BGB", definition: "Dokumentationspflicht" },
      { id: 3, term: "Expertenstandard", definition: "Fachlicher Maßstab" }
    ]
  },
  3: {
    title: "RACI-Modell verstehen",
    items: [
      { id: 1, term: "Responsible", definition: "Führt aus" },
      { id: 2, term: "Accountable", definition: "Verantwortlich" },
      { id: 3, term: "Consulted", definition: "Wird gefragt" },
      { id: 4, term: "Informed", definition: "Wird informiert" }
    ]
  }
};

const getModuleDragDrop = (moduleOrder, courseId) => {
  if (courseId === 'course-behandlungspflege-lg') {
    return lgDragDrops[moduleOrder];
  }
  if (courseId === 'course-weiterbildung-wundexperte') {
    return wundexperteDragDrops[moduleOrder];
  }
  if (courseId === 'course-pdl-weiterbildung-teil1' || courseId === 'course-pdl-weiterbildung-teil2') {
    return pdlDragDrops[moduleOrder];
  }
  // Jahresfortbildung Kurse
  if (courseId === 'course-hygiene-infektion') {
    return hygieneDragDrops[moduleOrder];
  }
  if (courseId === 'course-haftung-delegation') {
    return haftungDragDrops[moduleOrder];
  }
  if (courseId === 'course-gewaltpraevention') {
    return gewaltDragDrops[moduleOrder];
  }
  if (courseId === 'course-kommunikation') {
    return kommunikationDragDrops[moduleOrder];
  }
  if (courseId === 'course-rezertifizierung-43b') {
    return rezert43bDragDrops[moduleOrder];
  }
  // Pflegeberater Kurs
  if (courseId === 'course-pflegeberater') {
    return pflegeberaterDragDrops[moduleOrder];
  }
  // Praxisanleiter & Palliativpflege - Default zu leere Übung
  if (courseId === 'course-praxisanleiter' || courseId === 'course-palliativpflege' || courseId === 'course-pflege-ki') {
    return null; // Diese Kurse haben noch keine speziellen Drag&Drop-Übungen
  }
  return (courseId || '').includes('wund') ? wundDragDrops[moduleOrder] : null;
};

// Pflegeberater Drag & Drops
const pflegeberaterDragDrops = {
  1: {
    title: "Leistungen der Pflegeversicherung zuordnen",
    items: [
      { id: 1, term: "Pflegegeld", definition: "Geldleistung bei häuslicher Pflege durch Angehörige" },
      { id: 2, term: "Pflegesachleistung", definition: "Für professionelle Pflegedienste" },
      { id: 3, term: "Verhinderungspflege", definition: "Ersatzpflege bei Abwesenheit der Pflegeperson" },
      { id: 4, term: "Kurzzeitpflege", definition: "Vorübergehende vollstationäre Pflege" }
    ]
  },
  2: {
    title: "Pflegegrade und Beeinträchtigungen",
    items: [
      { id: 1, term: "Pflegegrad 1", definition: "Geringe Beeinträchtigungen (12,5-27 Punkte)" },
      { id: 2, term: "Pflegegrad 2", definition: "Erhebliche Beeinträchtigungen (27-47,5 Punkte)" },
      { id: 3, term: "Pflegegrad 3", definition: "Schwere Beeinträchtigungen (47,5-70 Punkte)" },
      { id: 4, term: "Pflegegrad 5", definition: "Schwerste Beeinträchtigungen (90-100 Punkte)" }
    ]
  },
  3: {
    title: "Case Management Phasen",
    items: [
      { id: 1, term: "Assessment", definition: "Bedarfserhebung und Analyse" },
      { id: 2, term: "Planung", definition: "Versorgungsplan erstellen" },
      { id: 3, term: "Durchführung", definition: "Koordination der Maßnahmen" },
      { id: 4, term: "Evaluation", definition: "Überprüfung und Anpassung" }
    ]
  }
};

// PDL Drag & Drops
const pdlDragDrops = {
  1: {
    title: "Pflegegrade zuordnen",
    items: [
      { id: 1, term: "Pflegegrad 1", definition: "Geringe Beeinträchtigung" },
      { id: 2, term: "Pflegegrad 2", definition: "Erhebliche Beeinträchtigung" },
      { id: 3, term: "Pflegegrad 3", definition: "Schwere Beeinträchtigung" },
      { id: 4, term: "Pflegegrad 5", definition: "Schwerste Beeinträchtigung" }
    ]
  },
  2: {
    title: "Führungsaufgaben der PDL",
    items: [
      { id: 1, term: "Planung", definition: "Dienstpläne, Einsatzplanung" },
      { id: 2, term: "Organisation", definition: "Abläufe strukturieren" },
      { id: 3, term: "Kontrolle", definition: "Qualität überwachen" },
      { id: 4, term: "Führung", definition: "Mitarbeiter anleiten" }
    ]
  }
};

// Wundexperte Drag & Drops
const wundexperteDragDrops = {
  1: {
    title: "Hautschichten zuordnen",
    items: [
      { id: 1, term: "Epidermis", definition: "Oberhaut - Schutzfunktion" },
      { id: 2, term: "Dermis", definition: "Lederhaut - Blutgefäße, Nerven" },
      { id: 3, term: "Subcutis", definition: "Unterhaut - Fettgewebe" }
    ]
  },
  2: {
    title: "Wundheilungsphasen",
    items: [
      { id: 1, term: "Exsudationsphase", definition: "Tag 0-4: Reinigung" },
      { id: 2, term: "Granulationsphase", definition: "Tag 4-21: Gewebeaufbau" },
      { id: 3, term: "Epithelisierung", definition: "Ab Tag 21: Hautneubildung" }
    ]
  },
  3: {
    title: "MEASURE-Konzept",
    items: [
      { id: 1, term: "M - Measure", definition: "Wunde messen" },
      { id: 2, term: "E - Exudate", definition: "Exsudat beurteilen" },
      { id: 3, term: "A - Appearance", definition: "Aussehen dokumentieren" },
      { id: 4, term: "S - Suffering", definition: "Schmerz erfassen" }
    ]
  },
  6: {
    title: "ABI-Werte interpretieren",
    items: [
      { id: 1, term: "ABI 0,9-1,3", definition: "Normal - Kompression möglich" },
      { id: 2, term: "ABI 0,5-0,8", definition: "pAVK - Modifizierte Kompression" },
      { id: 3, term: "ABI unter 0,5", definition: "Kritisch - KEINE Kompression!" }
    ]
  }
};

// ==================== JAHRESFORTBILDUNG DRAG & DROPS ====================
const hygieneDragDrops = {
  1: {
    title: "5 Momente der Händehygiene",
    items: [
      { id: 1, term: "Moment 1", definition: "VOR Patientenkontakt" },
      { id: 2, term: "Moment 2", definition: "VOR aseptischen Tätigkeiten" },
      { id: 3, term: "Moment 3", definition: "NACH Kontakt mit Körperflüssigkeiten" },
      { id: 4, term: "Moment 4", definition: "NACH Patientenkontakt" },
      { id: 5, term: "Moment 5", definition: "NACH Kontakt mit Patientenumgebung" }
    ]
  }
};

const haftungDragDrops = {
  1: {
    title: "Haftungsarten zuordnen",
    items: [
      { id: 1, term: "Zivilrechtlich", definition: "Schadensersatz, Schmerzensgeld" },
      { id: 2, term: "Strafrechtlich", definition: "Körperverletzung, Geldstrafe" },
      { id: 3, term: "Arbeitsrechtlich", definition: "Abmahnung, Kündigung" }
    ]
  }
};

const gewaltDragDrops = {
  1: {
    title: "Gewaltformen zuordnen",
    items: [
      { id: 1, term: "Körperliche Gewalt", definition: "Schlagen, Fixieren, Festhalten" },
      { id: 2, term: "Psychische Gewalt", definition: "Beschimpfen, Ignorieren, Drohen" },
      { id: 3, term: "Strukturelle Gewalt", definition: "Personalmangel, Zeitdruck" }
    ]
  }
};

const kommunikationDragDrops = {
  1: {
    title: "Kommunikationsregeln",
    items: [
      { id: 1, term: "Ich-Botschaft", definition: "Eigene Gefühle ausdrücken" },
      { id: 2, term: "Du-Botschaft", definition: "Angriff, Schuldzuweisung" },
      { id: 3, term: "Aktives Zuhören", definition: "Nachfragen, Zusammenfassen" }
    ]
  }
};

const rezert43bDragDrops = {
  1: {
    title: "Aufgaben zuordnen",
    items: [
      { id: 1, term: "Betreuungskraft", definition: "Aktivierung, Gespräche, Spiele" },
      { id: 2, term: "Pflegekraft", definition: "Körperpflege, Medikamente" },
      { id: 3, term: "Hauswirtschaft", definition: "Reinigung, Wäsche" }
    ]
  }
};

// LG Course Fill Blanks
const lgFillBlanks = {
  1: [
    {
      sentence: "Die Erstverordnung für Behandlungspflege ist ___ Tage gültig.",
      blank: "14",
      options: ["7", "14", "28", "30"],
      explanation: "Die Erstverordnung ist 14 Tage gültig, danach muss eine Folgeverordnung ausgestellt werden."
    }
  ],
  2: [
    {
      sentence: "Bei der Insulingabe müssen die ___ regelmäßig gewechselt werden.",
      blank: "Injektionsstellen",
      options: ["Spritzen", "Injektionsstellen", "Nadeln", "Zeiten"],
      explanation: "Regelmäßiger Wechsel der Injektionsstellen verhindert Lipodystrophie."
    }
  ]
};

const wundFillBlanks = {
  1: [
    {
      sentence: "Eine Wunde gilt als chronisch nach ___ Wochen ohne Heilungstendenz.",
      blank: "4-6",
      options: ["1-2", "4-6", "8-10", "12"],
      explanation: "Nach 4-6 Wochen ohne Heilungsfortschritt spricht man von einer chronischen Wunde."
    },
    {
      sentence: "Der Dekubitus entsteht durch die Formel: Druck × ___ = Gewebeschädigung",
      blank: "Zeit",
      options: ["Kraft", "Zeit", "Fläche", "Gewicht"],
      explanation: "Druck über Zeit führt zur Gewebeschädigung - deshalb ist regelmäßige Lagerung so wichtig!"
    }
  ],
  2: [
    {
      sentence: "Die Dokumentation muss mindestens ___ Jahre aufbewahrt werden.",
      blank: "10",
      options: ["5", "10", "15", "20"],
      explanation: "Die gesetzliche Aufbewahrungspflicht beträgt 10 Jahre nach Behandlungsabschluss."
    }
  ],
  3: [
    {
      sentence: "In Deutschland sind ca. ___ Millionen Menschen von chronischen Wunden betroffen.",
      blank: "2,7",
      options: ["1,2", "2,7", "4,5", "6,0"],
      explanation: "Etwa 2,7 Millionen Menschen leiden in Deutschland an chronischen Wunden."
    }
  ]
};

const getModuleFillBlanks = (moduleOrder, courseId) => {
  if (courseId === 'course-behandlungspflege-lg') {
    return lgFillBlanks[moduleOrder] || [];
  }
  if (courseId === 'course-weiterbildung-wundexperte') {
    return wundexperteFillBlanks[moduleOrder] || [];
  }
  if (courseId === 'course-pdl-weiterbildung-teil1' || courseId === 'course-pdl-weiterbildung-teil2') {
    return pdlFillBlanks[moduleOrder] || [];
  }
  // Jahresfortbildung Kurse
  if (courseId === 'course-hygiene-infektion') {
    return hygieneFillBlanks[moduleOrder] || [];
  }
  if (courseId === 'course-haftung-delegation') {
    return haftungFillBlanks[moduleOrder] || [];
  }
  if (courseId === 'course-gewaltpraevention') {
    return gewaltFillBlanks[moduleOrder] || [];
  }
  if (courseId === 'course-kommunikation') {
    return kommunikationFillBlanks[moduleOrder] || [];
  }
  if (courseId === 'course-rezertifizierung-43b') {
    return rezert43bFillBlanks[moduleOrder] || [];
  }
  // Pflegeberater Kurs
  if (courseId === 'course-pflegeberater') {
    return pflegeberaterFillBlanks[moduleOrder] || [];
  }
  // Praxisanleiter & Palliativpflege
  if (courseId === 'course-praxisanleiter' || courseId === 'course-palliativpflege' || courseId === 'course-pflege-ki') {
    return []; // Diese Kurse haben noch keine speziellen FillBlanks
  }
  return (courseId || '').includes('wund') ? (wundFillBlanks[moduleOrder] || []) : [];
};

// Pflegeberater Fill Blanks
const pflegeberaterFillBlanks = {
  1: [
    {
      sentence: "Der Anspruch auf Pflegeberatung ist in § ___ SGB XI geregelt.",
      blank: "7a",
      options: ["7", "7a", "14", "37"],
      explanation: "§7a SGB XI regelt den Rechtsanspruch auf individuelle Pflegeberatung."
    }
  ],
  2: [
    {
      sentence: "Seit 2017 gibt es ___ Pflegegrade statt der früheren Pflegestufen.",
      blank: "5",
      options: ["3", "4", "5", "6"],
      explanation: "Mit dem PSG II wurden 5 Pflegegrade eingeführt."
    }
  ],
  3: [
    {
      sentence: "Der ___ dokumentiert individuelle Ziele und Maßnahmen für die Versorgung.",
      blank: "Versorgungsplan",
      options: ["Dienstplan", "Versorgungsplan", "Medikamentenplan", "Speiseplan"],
      explanation: "Der Versorgungsplan ist das zentrale Dokument der individuellen Bedarfsplanung."
    }
  ]
};

// PDL Fill Blanks
const pdlFillBlanks = {
  1: [
    {
      sentence: "Die PDL trägt die ___ für die pflegerische Versorgung.",
      blank: "Gesamtverantwortung",
      options: ["Teilverantwortung", "Gesamtverantwortung", "Keine Verantwortung", "Mitverantwortung"],
      explanation: "Die PDL trägt die Gesamtverantwortung für alle pflegerischen Belange der Einrichtung."
    }
  ],
  2: [
    {
      sentence: "Pflegedokumentationen müssen ___ Jahre aufbewahrt werden.",
      blank: "10",
      options: ["5", "10", "15", "30"],
      explanation: "Die gesetzliche Aufbewahrungsfrist beträgt 10 Jahre."
    }
  ]
};

// Wundexperte Fill Blanks
const wundexperteFillBlanks = {
  1: [
    {
      sentence: "Die Haut besteht aus ___ Schichten.",
      blank: "3",
      options: ["2", "3", "4", "5"],
      explanation: "Die Haut hat 3 Schichten: Epidermis, Dermis und Subcutis."
    }
  ],
  2: [
    {
      sentence: "Die Granulationsphase dauert vom ___ bis zum 21. Tag.",
      blank: "4.",
      options: ["1.", "4.", "7.", "14."],
      explanation: "Die Granulationsphase beginnt am 4. Tag nach der Verletzung."
    }
  ],
  6: [
    {
      sentence: "Kompression ist kontraindiziert bei ABI unter ___.",
      blank: "0,5",
      options: ["0,3", "0,5", "0,8", "1,0"],
      explanation: "Bei ABI unter 0,5 liegt eine kritische Ischämie vor - keine Kompression!"
    }
  ],
  9: [
    {
      sentence: "___ Prozent aller chronischen Wunden haben Biofilm.",
      blank: "80",
      options: ["40", "60", "80", "95"],
      explanation: "80% der chronischen Wunden haben Biofilm, der regelmäßig entfernt werden muss."
    }
  ]
};

// ==================== JAHRESFORTBILDUNG FILL BLANKS ====================
const hygieneFillBlanks = {
  1: [
    {
      sentence: "Die hygienische Händedesinfektion dauert mindestens ___ Sekunden.",
      blank: "30",
      options: ["10", "20", "30", "60"],
      explanation: "Die Einwirkzeit muss mindestens 30 Sekunden betragen."
    }
  ]
};

const haftungFillBlanks = {
  1: [
    {
      sentence: "Die ___ bleibt beim Delegierenden, auch wenn die Aufgabe übertragen wird.",
      blank: "Verantwortung",
      options: ["Arbeit", "Verantwortung", "Bezahlung", "Zeit"],
      explanation: "Bei Delegation bleibt die Verantwortung beim Delegierenden."
    }
  ]
};

const gewaltFillBlanks = {
  1: [
    {
      sentence: "Gewalt durch Personalmangel nennt man ___ Gewalt.",
      blank: "strukturelle",
      options: ["körperliche", "psychische", "strukturelle", "verbale"],
      explanation: "Strukturelle Gewalt entsteht durch organisatorische Rahmenbedingungen."
    }
  ]
};

const kommunikationFillBlanks = {
  1: [
    {
      sentence: "Bei ___ drücke ich meine eigenen Gefühle aus statt den anderen anzugreifen.",
      blank: "Ich-Botschaften",
      options: ["Du-Botschaften", "Ich-Botschaften", "Wir-Botschaften", "Man-Botschaften"],
      explanation: "Ich-Botschaften vermeiden Schuldzuweisungen und fördern gute Kommunikation."
    }
  ]
};

const rezert43bFillBlanks = {
  1: [
    {
      sentence: "Betreuungskräfte müssen jährlich mindestens ___ Stunden Fortbildung nachweisen.",
      blank: "16",
      options: ["8", "16", "24", "40"],
      explanation: "Die jährliche Fortbildungspflicht beträgt 16 Stunden."
    }
  ]
};

// LG Course Scenarios
const lgScenarios = {
  1: {
    scenario: "Herr Schmidt (72 Jahre) benötigt täglich Insulin. Sie kommen zum Hausbesuch und bemerken, dass er die Injektion bereits selbst durchgeführt hat.",
    question: "Wie reagieren Sie?",
    choices: [
      {
        text: "Dokumentieren, dass der Patient die Injektion selbst gegeben hat",
        isCorrect: true,
        outcome: "Richtig! Die Dokumentation ist wichtig, auch wenn der Patient selbst injiziert hat.",
        learningPoint: "Jede Behandlungspflege-Maßnahme muss dokumentiert werden, auch wenn sie vom Patienten selbst durchgeführt wurde."
      },
      {
        text: "Nichts dokumentieren, da Sie nicht selbst gespritzt haben",
        isCorrect: false,
        outcome: "Falsch! Auch die Selbstinjektion des Patienten muss dokumentiert werden.",
        learningPoint: "Die Dokumentation ist Pflicht, unabhängig davon, wer die Maßnahme durchführt."
      },
      {
        text: "Trotzdem nochmal Insulin geben",
        isCorrect: false,
        outcome: "Gefährlich! Doppelte Insulingabe kann zu schwerer Hypoglykämie führen.",
        learningPoint: "Niemals doppelt dosieren - Hypoglykämie ist lebensbedrohlich!"
      }
    ]
  },
  2: {
    scenario: "Bei Frau Weber sollen Sie laut Verordnung Kompressionsstrümpfe anziehen. Sie klagt heute über starke Beinschmerzen.",
    question: "Was tun Sie?",
    choices: [
      {
        text: "Strümpfe trotzdem anziehen - es ist ja verordnet",
        isCorrect: false,
        outcome: "Riskant! Bei Schmerzen könnte eine Thrombose vorliegen.",
        learningPoint: "Bei ungewöhnlichen Symptomen immer erst abklären, nicht blind der Verordnung folgen."
      },
      {
        text: "Strümpfe nicht anziehen und Arzt informieren",
        isCorrect: true,
        outcome: "Richtig! Starke Beinschmerzen können auf eine Thrombose hinweisen - Arzt muss informiert werden.",
        learningPoint: "Bei Verdacht auf Komplikationen: Maßnahme unterbrechen und Arzt konsultieren."
      },
      {
        text: "Abwarten bis morgen",
        isCorrect: false,
        outcome: "Gefährlich! Eine Thrombose ist ein Notfall.",
        learningPoint: "Bei Thromboseverdacht sofort handeln - Lebensgefahr!"
      }
    ]
  }
};

const wundScenarios = {
  1: {
    scenario: "Frau Müller (78 Jahre) liegt seit 3 Tagen nur im Bett. Bei der Hautinspektion entdecken Sie eine nicht wegdrückbare Rötung am Sakralbereich.",
    question: "Was ist Ihr erster Schritt?",
    choices: [
      {
        text: "Sofort einen Schaumverband anlegen",
        isCorrect: false,
        outcome: "Ein Verband wäre verfrüht. Bei Grad 1 geht es primär um Druckentlastung.",
        learningPoint: "Bei Dekubitus Grad 1 ist Druckentlastung die wichtigste Maßnahme!"
      },
      {
        text: "Druckentlastung einleiten und dokumentieren",
        isCorrect: true,
        outcome: "Richtig! Druckentlastung ist bei Grad 1 die wichtigste Maßnahme. Die Dokumentation sichert Sie rechtlich ab.",
        learningPoint: "Bei Dekubitus Grad 1 (nicht wegdrückbare Rötung) ist Druckentlastung + Dokumentation der erste Schritt."
      },
      {
        text: "Abwarten und morgen nochmal schauen",
        isCorrect: false,
        outcome: "Gefährlich! Ohne Intervention kann sich die Situation schnell verschlechtern.",
        learningPoint: "Niemals abwarten! Jede Stunde zählt bei der Dekubitusprävention."
      }
    ]
  },
  2: {
    scenario: "Ein Kollege hat bei einem Patienten den Verbandswechsel durchgeführt, aber vergessen zu dokumentieren. Er bittet Sie, es 'kurz nachzutragen'.",
    question: "Wie reagieren Sie?",
    choices: [
      {
        text: "Ich trage es für ihn nach, er war ja dabei",
        isCorrect: false,
        outcome: "Das ist rechtlich problematisch! Sie dokumentieren etwas, das Sie nicht selbst durchgeführt haben.",
        learningPoint: "Nur dokumentieren, was Sie selbst beobachtet oder durchgeführt haben!"
      },
      {
        text: "Er soll es selbst nachtragen mit Vermerk 'nachträglich dokumentiert'",
        isCorrect: true,
        outcome: "Korrekt! Nachträgliche Dokumentation ist erlaubt, muss aber als solche gekennzeichnet werden.",
        learningPoint: "Nachträgliche Einträge müssen als solche erkennbar sein (§ 630f BGB)."
      },
      {
        text: "Das ist nicht so wichtig, kann er beim nächsten Mal machen",
        isCorrect: false,
        outcome: "Sehr riskant! Fehlende Dokumentation führt zur Beweislastumkehr.",
        learningPoint: "Was nicht dokumentiert ist, hat rechtlich nicht stattgefunden!"
      }
    ]
  }
};

const getModuleScenario = (moduleOrder, courseId) => {
  if (courseId === 'course-behandlungspflege-lg') {
    return lgScenarios[moduleOrder];
  }
  if (courseId === 'course-weiterbildung-wundexperte') {
    return wundexperteScenarios[moduleOrder];
  }
  if (courseId === 'course-pdl-weiterbildung-teil1' || courseId === 'course-pdl-weiterbildung-teil2') {
    return pdlScenarios[moduleOrder];
  }
  // Jahresfortbildung Kurse
  if (courseId === 'course-hygiene-infektion') {
    return hygieneScenarios[moduleOrder];
  }
  if (courseId === 'course-haftung-delegation') {
    return haftungScenarios[moduleOrder];
  }
  if (courseId === 'course-gewaltpraevention') {
    return gewaltScenarios[moduleOrder];
  }
  if (courseId === 'course-kommunikation') {
    return kommunikationScenarios[moduleOrder];
  }
  if (courseId === 'course-rezertifizierung-43b') {
    return rezert43bScenarios[moduleOrder];
  }
  // Pflegeberater Kurs
  if (courseId === 'course-pflegeberater') {
    return pflegeberaterScenarios[moduleOrder];
  }
  // Praxisanleiter & Palliativpflege
  if (courseId === 'course-praxisanleiter' || courseId === 'course-palliativpflege' || courseId === 'course-pflege-ki') {
    return null; // Diese Kurse haben noch keine speziellen Szenarien
  }
  return (courseId || '').includes('wund') ? wundScenarios[moduleOrder] : null;
};

// Pflegeberater Scenarios
const pflegeberaterScenarios = {
  1: {
    scenario: "Eine 78-jährige Frau wurde kürzlich als pflegebedürftig eingestuft (Pflegegrad 2). Ihre Tochter ruft an und ist völlig überfordert. Sie weiß nicht, welche Leistungen ihr zustehen.",
    question: "Wie gestalten Sie das erste Beratungsgespräch?",
    choices: [
      {
        text: "Am Telefon alle Leistungen aufzählen und eine Broschüre zusenden",
        isCorrect: false,
        outcome: "Zu unpersönlich und überfordernd. Die Tochter kann die Informationsflut nicht verarbeiten.",
        learningPoint: "Pflegeberatung erfordert persönliche Gespräche und schrittweise Information."
      },
      {
        text: "Hausbesuch vereinbaren, Situation erfassen, Versorgungsplan gemeinsam entwickeln",
        isCorrect: true,
        outcome: "Richtig! Sie verschaffen sich vor Ort einen Überblick und können individuell beraten.",
        learningPoint: "Die persönliche Situationsanalyse ist Grundlage für passgenaue Beratung."
      },
      {
        text: "Direkt einen Pflegedienst empfehlen und die Formalitäten übernehmen",
        isCorrect: false,
        outcome: "Zu schnell! Erst muss der individuelle Bedarf ermittelt werden.",
        learningPoint: "Pflegeberatung ist ergebnisoffen - die Entscheidung liegt beim Ratsuchenden."
      }
    ]
  },
  2: {
    scenario: "Bei der Begutachtung wurde ein niedrigerer Pflegegrad festgestellt als erwartet. Die Familie ist unzufrieden und möchte Widerspruch einlegen.",
    question: "Wie beraten Sie die Familie?",
    choices: [
      {
        text: "Von Widerspruch abraten, da er selten Erfolg hat",
        isCorrect: false,
        outcome: "Falsch! Widersprüche haben durchaus Erfolgsaussichten.",
        learningPoint: "Ca. 40% aller Widersprüche führen zu einer Höherstufung."
      },
      {
        text: "Begutachtungsergebnis analysieren, Widerspruch mit Pflegetagebuch unterstützen",
        isCorrect: true,
        outcome: "Richtig! Ein gut begründeter Widerspruch mit Dokumentation hat gute Chancen.",
        learningPoint: "Das Pflegetagebuch dokumentiert den tatsächlichen Hilfebedarf im Alltag."
      },
      {
        text: "Sofort einen Anwalt empfehlen",
        isCorrect: false,
        outcome: "Ein Anwalt ist erst bei Klage nötig, nicht beim Widerspruch.",
        learningPoint: "Der Widerspruch kann von Angehörigen selbst oder mit Beratungsunterstützung eingelegt werden."
      }
    ]
  },
  3: {
    scenario: "Ein Pflegebedürftiger mit Demenz soll vom Krankenhaus nach Hause entlassen werden. Die Ehefrau ist verzweifelt, da die Wohnung nicht pflegegerecht ist.",
    question: "Welche Maßnahmen koordinieren Sie?",
    choices: [
      {
        text: "Kurzzeitpflege empfehlen, bis die Wohnung angepasst ist",
        isCorrect: true,
        outcome: "Richtig! Kurzzeitpflege überbrückt die Zeit für Wohnraumanpassung und Organisation.",
        learningPoint: "Kurzzeitpflege kann bis zu 8 Wochen pro Jahr genutzt werden."
      },
      {
        text: "Sofort Pflegeheimplatz suchen",
        isCorrect: false,
        outcome: "Zu früh! Erst sollten ambulante Möglichkeiten geprüft werden.",
        learningPoint: "Ambulant vor stationär ist ein Grundsatz der Pflegeversicherung."
      },
      {
        text: "Entlassung verzögern lassen",
        isCorrect: false,
        outcome: "Nicht möglich. Krankenhäuser entlassen, sobald medizinisch vertretbar.",
        learningPoint: "Entlassmanagement muss frühzeitig geplant werden."
      }
    ]
  }
};

// PDL Scenarios
const pdlScenarios = {
  1: {
    scenario: "Eine Pflegekraft meldet sich krank. Der Dienstplan ist bereits erstellt und es gibt keine Vertretung.",
    question: "Wie reagieren Sie als PDL?",
    choices: [
      {
        text: "Den Dienst selbst übernehmen",
        isCorrect: false,
        outcome: "Als PDL sollten Sie primär koordinieren, nicht selbst einspringen.",
        learningPoint: "Die PDL sollte die Personalplanung so gestalten, dass Ausfälle kompensiert werden können."
      },
      {
        text: "Personalreserve aktivieren und Dienstplan anpassen",
        isCorrect: true,
        outcome: "Richtig! Aktivieren Sie die Personalreserve und dokumentieren Sie die Änderung.",
        learningPoint: "Eine gute Personalplanung beinhaltet immer eine Reserve für Ausfälle."
      },
      {
        text: "Die Schicht unterbesetzt laufen lassen",
        isCorrect: false,
        outcome: "Gefährlich! Unterbesetzung gefährdet Bewohner und Mitarbeiter.",
        learningPoint: "Mindestpersonalbesetzung muss immer gewährleistet sein."
      }
    ]
  },
  2: {
    scenario: "Bei einer MD-Prüfung werden Mängel in der Dokumentation festgestellt.",
    question: "Wie gehen Sie vor?",
    choices: [
      {
        text: "Die Mängel bestreiten",
        isCorrect: false,
        outcome: "Bestreiten hilft nicht und verschlechtert die Situation.",
        learningPoint: "Konstruktive Zusammenarbeit mit dem MD ist wichtig."
      },
      {
        text: "Mängel anerkennen und Maßnahmenplan erstellen",
        isCorrect: true,
        outcome: "Richtig! Erstellen Sie einen konkreten Maßnahmenplan mit Verantwortlichen und Fristen.",
        learningPoint: "Ein Maßnahmenplan zeigt, dass Sie die Probleme ernst nehmen und lösen wollen."
      },
      {
        text: "Abwarten bis zur nächsten Prüfung",
        isCorrect: false,
        outcome: "Falsch! Ohne Maßnahmen werden die Mängel bei der nächsten Prüfung wieder beanstandet.",
        learningPoint: "Proaktives Handeln verhindert Wiederholungsfehler."
      }
    ]
  }
};

// Wundexperte Scenarios
const wundexperteScenarios = {
  1: {
    scenario: "Beim Verbandwechsel bemerken Sie, dass die Wunde übel riecht und grünliches Sekret absondert.",
    question: "Was ist Ihr erster Schritt?",
    choices: [
      {
        text: "Wundabstrich entnehmen und Arzt informieren",
        isCorrect: true,
        outcome: "Richtig! Bei Infektionszeichen: Abstrich und ärztliche Konsultation.",
        learningPoint: "Grünes Sekret und Geruch sind klassische Infektionszeichen - Arzt einschalten!"
      },
      {
        text: "Antibiotische Salbe auftragen",
        isCorrect: false,
        outcome: "Ohne Arztanordnung keine Antibiotika! Zudem wäre ein Abstrich vorher nötig.",
        learningPoint: "Antibiotika nur nach ärztlicher Anordnung und nach Abstrich."
      },
      {
        text: "Abwarten ob es besser wird",
        isCorrect: false,
        outcome: "Gefährlich! Eine Wundinfektion kann sich schnell ausbreiten.",
        learningPoint: "Bei Infektionszeichen niemals abwarten!"
      }
    ]
  },
  6: {
    scenario: "Sie sollen bei einem Patienten mit Unterschenkelulkus eine Kompression anlegen. Der ABI wurde nicht gemessen.",
    question: "Wie verhalten Sie sich?",
    choices: [
      {
        text: "Kompression anlegen, wurde ja vom Arzt verordnet",
        isCorrect: false,
        outcome: "Ohne ABI-Messung kann Kompression bei pAVK lebensbedrohlich sein!",
        learningPoint: "KEINE Kompression ohne ABI - auch bei ärztlicher Verordnung nachfragen!"
      },
      {
        text: "ABI-Messung veranlassen, dann erst Kompression",
        isCorrect: true,
        outcome: "Richtig! ABI muss vor jeder Kompression bekannt sein.",
        learningPoint: "ABI unter 0,5 = absolute Kontraindikation für Kompression."
      },
      {
        text: "Nur leichte Kompression anlegen zur Sicherheit",
        isCorrect: false,
        outcome: "Auch leichte Kompression kann bei kritischer Ischämie schaden!",
        learningPoint: "Es gibt keine 'sichere' Kompression ohne ABI-Kenntnis."
      }
    ]
  },
  7: {
    scenario: "Ein Diabetiker hat ein Ulkus an der Fußsohle. Er trägt normale Schuhe und möchte keine speziellen Schuhe.",
    question: "Was tun Sie?",
    choices: [
      {
        text: "Seine Entscheidung akzeptieren und nur die Wunde versorgen",
        isCorrect: false,
        outcome: "Ohne Druckentlastung wird das Ulkus nicht heilen!",
        learningPoint: "Patientenedukation ist Teil der Wundversorgung."
      },
      {
        text: "Aufklären über die Notwendigkeit der Druckentlastung",
        isCorrect: true,
        outcome: "Richtig! Erklären Sie, dass ohne Druckentlastung keine Heilung möglich ist.",
        learningPoint: "Druckentlastung ist beim DFS die wichtigste Maßnahme - ohne sie heilt nichts."
      },
      {
        text: "Den Arzt informieren, der soll es ihm sagen",
        isCorrect: false,
        outcome: "Aufklärung ist auch Ihre Aufgabe als Wundexperte.",
        learningPoint: "Wundexperten haben eine Edukationspflicht gegenüber Patienten."
      }
    ]
  }
};

// ==================== JAHRESFORTBILDUNG SCENARIOS ====================
const hygieneScenarios = {
  1: {
    scenario: "Sie betreten das Zimmer eines MRSA-positiven Bewohners. Ein Kollege hat den Kittel nicht angezogen.",
    question: "Wie reagieren Sie?",
    choices: [
      {
        text: "Nichts sagen, will keinen Ärger",
        isCorrect: false,
        outcome: "Falsch! Schweigen gefährdet andere Bewohner und Mitarbeiter.",
        learningPoint: "Hygieneverstöße müssen angesprochen werden."
      },
      {
        text: "Kollegen freundlich auf die Schutzkleidung hinweisen",
        isCorrect: true,
        outcome: "Richtig! Kollegiale Korrektur schützt alle Beteiligten.",
        learningPoint: "Jeder ist für die Einhaltung der Hygieneregeln verantwortlich."
      },
      {
        text: "Sofort die PDL informieren",
        isCorrect: false,
        outcome: "Übertrieben als erste Reaktion - erst kollegiales Gespräch.",
        learningPoint: "Erst direkte Ansprache, bei Wiederholung Vorgesetzte einschalten."
      }
    ]
  }
};

const haftungScenarios = {
  1: {
    scenario: "Eine Pflegehelferin fragt, ob sie die Medikamente richten darf. Sie hat keine entsprechende Schulung.",
    question: "Wie reagieren Sie?",
    choices: [
      {
        text: "Ja, sie soll es einfach versuchen",
        isCorrect: false,
        outcome: "Gefährlich! Ohne Qualifikation keine Delegation möglich.",
        learningPoint: "Delegation erfordert entsprechende Qualifikation des Mitarbeiters."
      },
      {
        text: "Nein, erst Schulung dann Delegation",
        isCorrect: true,
        outcome: "Richtig! Ohne Qualifikation keine Delegation.",
        learningPoint: "Die Qualifikation des Mitarbeiters ist Voraussetzung für Delegation."
      },
      {
        text: "Ja, wenn ich dabei zuschaue",
        isCorrect: false,
        outcome: "Auch unter Aufsicht ist das Richten von Medikamenten ohne Schulung nicht erlaubt.",
        learningPoint: "Medikamentenmanagement erfordert nachgewiesene Kompetenz."
      }
    ]
  }
};

const gewaltScenarios = {
  1: {
    scenario: "Ein Bewohner wird beim Essen ungeduldig und wirft das Geschirr. Ein Kollege sagt: 'Dann bekommt er eben nichts mehr!'",
    question: "Was tun Sie?",
    choices: [
      {
        text: "Dem Kollegen zustimmen",
        isCorrect: false,
        outcome: "Nahrungsentzug ist eine Form von Gewalt!",
        learningPoint: "Nahrungsverweigerung als Strafe ist Gewalt gegen Schutzbefohlene."
      },
      {
        text: "Situation deeskalieren und Ursache klären",
        isCorrect: true,
        outcome: "Richtig! Erst Situation beruhigen, dann Ursache für das Verhalten klären.",
        learningPoint: "Herausforderndes Verhalten hat immer eine Ursache - diese gilt es zu verstehen."
      },
      {
        text: "Ignorieren und weitermachen",
        isCorrect: false,
        outcome: "Falsch! Gewaltandrohung darf nicht ignoriert werden.",
        learningPoint: "Bei Gewaltandrohung müssen Sie einschreiten."
      }
    ]
  }
};

const kommunikationScenarios = {
  1: {
    scenario: "Eine Angehörige beschwert sich lautstark über die Pflege ihrer Mutter. Andere Besucher schauen bereits.",
    question: "Wie reagieren Sie?",
    choices: [
      {
        text: "Sie lautstark zur Ruhe auffordern",
        isCorrect: false,
        outcome: "Eskaliert die Situation nur weiter.",
        learningPoint: "Auf Emotionen nicht mit Gegenemotionen reagieren."
      },
      {
        text: "In einen ruhigen Raum bitten und aktiv zuhören",
        isCorrect: true,
        outcome: "Richtig! Erst Atmosphäre wechseln, dann aktiv zuhören.",
        learningPoint: "Beschwerden ernst nehmen, ruhige Umgebung schaffen, zuhören."
      },
      {
        text: "Die PDL holen und weggehen",
        isCorrect: false,
        outcome: "Vermeidung löst das Problem nicht und wirkt unprofessionell.",
        learningPoint: "Konflikte selbst ansprechen, erst bei Eskalation Vorgesetzte einschalten."
      }
    ]
  }
};

const rezert43bScenarios = {
  1: {
    scenario: "Eine Bewohnerin möchte nicht an der Gruppenaktivität teilnehmen und weint.",
    question: "Was tun Sie als Betreuungskraft?",
    choices: [
      {
        text: "Sie zur Teilnahme überreden",
        isCorrect: false,
        outcome: "Zwang ist keine Betreuung.",
        learningPoint: "Die Teilnahme an Aktivitäten ist freiwillig."
      },
      {
        text: "Einzelgespräch anbieten und Grund erfragen",
        isCorrect: true,
        outcome: "Richtig! Erst verstehen, warum sie nicht möchte.",
        learningPoint: "Betreuung orientiert sich an den Bedürfnissen der Bewohner."
      },
      {
        text: "Sie in Ruhe lassen und zur Gruppe gehen",
        isCorrect: false,
        outcome: "Eine weinende Person sollte nicht allein gelassen werden.",
        learningPoint: "Emotionale Unterstützung ist Teil der Betreuungsaufgabe."
      }
    ]
  }
};

// CSS for 3D flip effect (needs to be added to index.css or as style tag)
const flipStyles = `
  .perspective-1000 { perspective: 1000px; }
  .transform-style-preserve-3d { transform-style: preserve-3d; }
  .backface-hidden { backface-visibility: hidden; }
  .rotate-y-180 { transform: rotateY(180deg); }
`;

export default InteractiveLearning;
export { FlashCard, MiniCheckpoint, DragDropExercise, FillInTheBlank, ScenarioLearning, AudioSection };
